import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { Badge, Button, Card, CardBody, CardHead, Chips, Drawer, DurationInput, Field, Segmented, Select, Textarea } from '@/components/ui'
import { Icon } from '@/components/icons'
import { RefUploader } from '@/components/RefUploader'
import { ProductInfoSection } from '@/components/ProductInfoSection'
import { VoiceLibrary } from '@/components/VoiceLibrary'
import { AudioPlayerBar } from '@/components/AudioPlayerBar'
import { DEFAULT_PLATFORM_ID, DOMESTIC_PLATFORMS, OVERSEAS_PLATFORMS, getPlatform } from '@/config/platforms'
import {
  AVATAR_SOURCES,
  DIGITAL_BOARD_APP_SLUG,
  DIGITAL_BOARD_FAMILY,
  DIGITAL_BOARD_OUTPUTS,
  DIGITAL_HUMAN_MODEL_SLUG,
  DIGITAL_HUMAN_MODES,
  DIGITAL_HUMAN_USES,
  type AvatarSource,
  type DigitalHumanUse,
} from '@/config/catalog'
import { DEFAULT_VOICE_ID, pickVoiceId } from '@/config/voices'
import { buildDigitalBoardPrompt, buildDigitalHumanPrompt, buildHumanFillBrief } from '@/config/prompts'
import { AiFillButton } from '@/components/AiFillButton'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useJobCenter, isActive, type Job } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { FILL_FORM_FIELDS } from '@/api/types'
import { spokenText } from '@/lib/spoken'
import { refillFromInputs } from '@/lib/refill'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'
import { useVoices } from '@/hooks/useVoices'
import { estimateAudioMs } from '@/config/catalog'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { useToast } from '@/context/ToastContext'

type Mode = 'talking-head' | 'clone-avatar'

type FormState = {
  mode: Mode
  /** 口播用途：讲解型 / 带货型 —— 同一个模型、同一套参数，差别在稿子的意图 */
  use: DigitalHumanUse
  /**
   * ⚠️ 这里**没有** `avatarSource`。
   *
   * 它是「形象的来源」（AI 模特 / 克隆真人），但不该是可编辑的状态 ——
   * 由 `mode` 唯一决定（形象克隆 → clone，数字人口播 → ai），见页面里的派生常量。
   * 放在 FormState 里就得再写一个 useEffect 把它和 mode 对齐，
   * 而两个字段互相 patch 的代码必然能构造出「模式是口播、来源却是克隆」的组合。
   */
  /**
   * 画面描述（2026-10-06 由「出镜要求」扩写）。
   *
   * ⚠️ 2026-10-06 八改后它**只喂一处**：生成分镜图时的生图提示词
   * （场景 / 动作 / 光线，也就是画面里那个人在做什么）。
   *
   * 上一版它还喂第二处 —— 数字人出片时进上游的动作提示词节点
   * （i2v 档 default app 的 114，字段名 `positive_prompt`）。换成 v2v 之后
   * **那个节点不存在了**（v2v 的 app 只有推广位/视频/音频三个输入），
   * 于是「它决定镜头与表情」这句话不再成立。字段留着、含义收窄到一处 ——
   * 因为动作确实仍然要由它决定，只是决定的路径变成了「写进分镜图 → 分镜图长成那样」。
   *
   * 它叫「画面描述」而不是「出镜要求」，是因为用户的原话是
   * 「选好模特，然后给点描述（**场景，动作**等等），生成个分镜图」——
   * 描述的是画面本身。改动作的入口是**重生成分镜图**，不是这段文字。
   */
  shotNote: string
  /**
   * 音色 —— **存的就是上游音色 ID**（如 `Chinese (Mandarin)_Warm_Girl`）。
   *
   * 2026-10-06 之前存的是本地编的短 id（`female-warm`），再由 `VOICE_OPTIONS[].tts`
   * 翻成上游 ID。那层映射已删：清单真源在 `GET /api/aigc/voices`
   * （默认引擎 ⇒ `apilio-tts` skill 的 `SYSTEM_VOICES`，88 条），前端留一份子集必然漂移。
   * 旧草稿里的短 id 由 `pickVoiceId()` 归一（`LEGACY_VOICE_IDS` 全覆盖 + 换引擎时回落默认）。
   */
  voice: string
  script: string
  /**
   * 产品名称 —— **不是必填**（2026-10-06 用户口径：「产品不是必须选的」）。
   *
   * 但有它更准：AI 写稿靠它（没它就是一句「这款产品」式的空话），
   * 分镜图的画面描述也用它告诉模型手里拿的是什么。所以留 required 的**提示**、
   * 去掉了 required 的**阻断**。
   */
  productName: string
  duration: number
  aspectRatio: string
  platform: string
  /** 克隆授权确认 —— 真人出镜档不勾不让提交，这不是可选项 */
  authorized: boolean
  /**
   * **最终的出镜形象图** —— 数字人对口型真正吃的那一张，必填 1 张。
   *
   * 它的来源是「生成的分镜图」（见 `modelRefs` 那条注释里的三步），
   * 所以它排在上传区的**最后**：前面两步是它的素材，它才是产物。
   */
  refs: string[]
  /**
   * **模特图**（分镜图的底子）—— 2026-10-06 新增，必填 1 张。
   *
   * 用户口径：「应该是**选好模特**，然后给点描述，生成个分镜图」。
   * 取法与上一版的形象图完全一样（引用「AI 模特图」档的产出，或上传人像），
   * 只是它现在服务于「生成分镜图」这一步，而不是直接喂给对口型。
   */
  modelRefs: string[]
  /**
   * **产品图**（可选）—— 2026-10-06 新增。
   *
   * 用户口径：「产品不是必须选的，需要可以上传产品图」。
   * 它只进分镜图的参考图（让模特手里拿着这个东西），**绝不直接发给数字人接口** ——
   * 对口型上游只有一个 LoadImage 节点，多发的图会被适配器丢掉并记日志。
   */
  productRefs: string[]
  /**
   * 分镜图**一次出几张候选**（可改，2026-10-07）。
   *
   * 为什么它不是常量：用户看到「（4 张，独立计费）」第一反应就是「**怎么 4 张的？**」
   * —— 他要的是一张出镜画面，却按 4 张付钱。多出几张本来是为了**有得挑**
   * （生图模型一次出 N 张、挑最好的一张是行业常规做法），但那是**取舍**，
   * 取舍得由掏钱的人做，不由代码写死。
   *
   * 上一版是 `DIGITAL_BOARD_OUTPUTS = 4` 这个常量直接进报价与提交载荷，
   * 界面上连改的入口都没有 —— 用户只能接受。
   */
  boardOutputs: number
  /**
   * **这一次做口型用的那段配音**（已有音频的地址，空 = 还没选）。
   *
   * 🔴 2026-10-09 加。口径是用户在页面上定的：**先在本页把配音合出来听一遍，
   * 确认了再拿去做口型** —— 一次 TTS、一次花费，试听的与成片里的是同一条。
   *
   * 在这之前，TTS 是**藏在上游里面**的：用户写「0-3s：你家厨房台面还在用抹布擦水吗」，
   * 点生成，等几分钟，成片出来才知道那句话被念成了什么样（断句、多音字、语速）。
   * 而失败一次的钱是按「TTS + 对口型」整档收的。
   *
   * ⚠️ 它**不是**「音色」的替代品：音色是「用谁的声音」，这一格是「这段声音本身」。
   * 两者都留着，因为引用了别处的配音时音色不再生效（见 `audioMatchesScript`）。
   */
  audio: string
  /**
   * 上面那条配音是**照着哪一版口播稿**合出来的。
   *
   * 🔴 少了它就会出现本项目最难查的那一类错：用户合完配音又把稿子改了两句，
   * 提交时带着旧音频 —— 成片里念的是**上一版台词**，而界面上、任务记录里、
   * 确认弹窗里写的都是新稿子。任务状态全绿，只有听成片才发现。
   * 有了它，页面就能当场说出「这条配音已经不是你现在的稿子了」。
   */
  audioText: string
}

const DEFAULTS: FormState = {
  mode: 'talking-head',
  use: 'sell',
  shotNote: '',
  voice: DEFAULT_VOICE_ID,
  script: '',
  productName: '',
  duration: 15,
  aspectRatio: '9:16',
  platform: DEFAULT_PLATFORM_ID,
  authorized: false,
  refs: [],
  modelRefs: [],
  productRefs: [],
  // 默认仍是 4（真源是 `catalog.ts` 的 `DIGITAL_BOARD_OUTPUTS`，行为与上一版一致）
  boardOutputs: DIGITAL_BOARD_OUTPUTS,
  audio: '',
  audioText: '',
}

/** 分镜图一次能出几张候选。1 张 = 不挑，直接用它（最省） */
const BOARD_OUTPUT_OPTIONS = [1, 2, 4]

/**
 * 分镜图的画面比例候选。
 *
 * ⚠️ **派生，不是第二份枚举**：比例的全部取值就在平台表里（`platforms.ts` 的
 * `sizes.video`），这里摊平去重即可。写死一份 `['9:16','16:9',…]` 的话，
 * 平台新增一个比例时这里不会跟着变 —— 而它恰恰是「出镜形象」的形状，
 * 也是成片比例的唯一来源。
 */
const BOARD_RATIOS = [...new Set([...DOMESTIC_PLATFORMS, ...OVERSEAS_PLATFORMS].map((p) => p.sizes.video))]

/**
 * 引擎只有这一档，写成人话给界面用（slug 见 `DIGITAL_HUMAN_MODEL_SLUG` 的注释）。
 *
 * 2026-10-06 八改：由 InfiniteTalk 的 i2v 档换成 **HeyGen v2v** 档（`heygen-v2v`）。
 * 换的理由是实测：同一段 11.5 秒素材，v2v 端到端 **91 秒 / 32 RH 币**，
 * 而 i2v 是 10 秒片约 8 分钟 / 93 币 —— 快约 5 倍、便宜约 3 倍。
 *
 * ⚠️ 界面文案不许提「v2v / 图生视频」这类实现词：用户关心的是「谁在念、多久出片」。
 * 但**必须如实**说的是：这一档的上游**不吃动作描述**（只有推广位/视频/音频三个节点）。
 * → 见下面「画面描述」那个字段的 hint，它已经不能再写「也作为出镜要求发给数字人接口」。
 */
const ENGINE_NAME = '数字人对口型（HeyGen v2v）'
const ENGINE_CHANNEL = 'RunningHub'

/**
 * 出片耗时估算 —— **从稿子时长现算，不能写成常量**（2026-10-06 八改）。
 *
 * 依据是实测：11.47 秒音频 → 生成 90.7s（约 **7.9 倍音频时长**），
 * 且换尺寸（688×1024 / 720×1280）耗时不变 ⇒ **耗时由音频长度决定**。
 * 再加一段固定开销（TTS 合成 + 两次上传 + 排队，实测约 20-30s，这里取 60s 留余量）。
 *
 * ⚠️ 为什么原来是常量（`active.estimatedMs = 480000`）而现在不行：
 * 这一档的时长选项是 15/30/60 秒，差 4 倍。一个常量按 15 秒算要虚报 5 分钟
 * （用户以为卡死了），按 60 秒算又要少报一半 —— 两头必有一头是假话。
 * 这个值直接驱动任务卡的剩余时间与进度条（`TaskCard` 读 `estimatedMs`）。
 */
const estimateMs = (seconds: number) => Math.round(seconds * 7900) + 60000

/**
 * ⚠️ 这里曾有个 `DEFAULT_SHOT_NOTE`（后端 `DEFAULT_ACTION` 那句「固定镜头…」），
 * 用来在 hint 里告诉用户「留空时发这句」。换成 v2v 之后**它没有地方可发了** ——
 * 这一档的上游只有「推广位 / 视频 / 音频」三个节点，动作描述没有节点可写。
 * 留着一个「留空时会兜底成这句话」的说明，就是在承诺一件不会发生的事。
 * 🔴 2026-10-07：后端那句 `DEFAULT_ACTION` **也删了** —— i2v 三套预设已按用户口径
 * 从 skill 移除，那个常量连同 `_node_list(face=, action=)` 两个形参一起消失，
 * 后端现在只剩 v2v 一条执行路径。所以这里没有任何「i2v 档还在用」的残留。
 */

/**
 * 数字人单独成页，不塞进视频生产的三个 mode 里。
 *
 * 理由：商品主图视频的输入是产品图，数字人的输入是「形象 + 口播稿」，
 * 连进料结构都不一样。混在一个下拉里，用户选了之后不知道该传什么。
 *
 * 2026-09-28 二改：用户反馈「左边的 form 太长了，操作不友好」——
 * 上一版只有 2 个页签，第 1 个塞了「模式 + 形象来源 + 形象素材 + 投放平台 +
 * 音色 + 口播稿 + 时长 + 比例 + 模型 + 产品名」共 10 个字段，左栏要滚很久。
 * 拆成三个页签后仍是「先定形象 → 再写稿 → 最后调参数并核对」。
 *
 * 2026-09-29 三改：用户再反馈「这个东西太多，form 太长了，不行就分 4 步」——
 * 上一版的第 1 页虽然叫「模式与形象」，实际堆了 8 块：
 * 模式 chips + 口播用途 + 用途说明 + 上游状态 + 形象来源 + 授权红线 +
 * 形象描述 + 素材上传。**「选产出形态」与「备物料」是两件事**，
 * 一个页面回答一个问题的规则在这里被破了，所以第 1 页特别长。
 * 现在把「模式」独立成第 1 页，按决策域切成四步：
 *   ① 模式（产出形态：出视频还是建档）→ ② 形象与素材（输入物）
 *   → ③ 口播与音色（内容决策）→ ④ 参数与生成（花钱与确认）
 * 页签名与卡标题不再重复：页签是「模式」，卡标题是「要做哪一种」。
 *
 * 2026-09-29 四改：用户原话「形象来源只需要上传素材，这做一个 form」——
 * 删掉「形象来源」那个二选一（它跟「模式」问的是同一件事，来源由模式推导），
 * 第 2 步合并成一张 form，提交栏从底部移到右上角。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-05 五改：接入 RunningHub InfiniteTalk —— 第 2 步不再靠文字生成形象
 * ---------------------------------------------------------------------------
 * 用户原话（圈着「形象与素材」页签、把「形象 / 产品素材」上传区划掉）：
 * **「这里直接引用 ai 模特或者上传人像才对」**。
 *
 * 这一版把上游接上了（`runninghub-infinitetalk`，见后端 registry），于是有两件事
 * 必须跟着改，否则界面会说出上游根本不认的话：
 *
 *   1. **形象描述下线**。对口型要的是**一张有五官结构的照片**（LoadImage 节点），
 *      不是一段描述长相的文字 —— 拿文字生成的脸去驱动口型，嘴型对不上、
 *      脸还会中途变形，而且它**不报错**。所以形象图从「可选素材」升为**必填主输入**，
 *      取法两条都在同一个上传器里：从素材库挑一张已生成的 AI 模特图（引用），
 *      或本地上传人像照片。**不再问第二次二选一** —— 同一张脸的两种取法不该问两遍。
 *   2. **原「形象描述」的位置改放「出镜要求」**（`shotNote`）—— 它真的会发出去
 *      （上游动作提示词节点），是这一页唯一还该留的自由文本。
 *      ⚠️ **八改（2026-10-06）之后这半句不成立了**：换成 v2v 档后上游没有动作节点，
 *      `shotNote` 只剩「拼进分镜图提示词」这一条出路。此处保留原文是记录当时的结论，
 *      当前口径以 `FormState.shotNote` 与上面 `PANES` 的八改注释为准。
 *
 * 同一轮一起改的两件小事（都是界面在说假话）：
 *   · 「比例」控件撤掉 —— 成片比例跟随形象图，**数字人这一步**的上游不接受指定，
 *     留着就是个假控件。⚠️ 2026-10-07 补充：撤的是**这一步**的控件，不是比例这件事
 *     本身 —— 比例的真实入口在**分镜图那一步**（生图接受指定，出成什么形状的
 *     形象图，成片就是什么比例）。用户回了一句「**比例也没有**」正是因为
 *     两边都没有：数字人这步不许指定、分镜图那步又没长控件。
 *   · 「时长」保留，但它现在管的是**稿子写在多长里**（报价也按它算），
 *     成片长度由音频实际长度决定，hint 里如实写明。
 * 模型下拉也撤了 —— 数字人只有这一个引擎，`ModelSelect` 里放一条等于没有选择。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-06 六改：第 1、2 步合成一步；形象图直接引用 AI 模特；音色走接口
 * ---------------------------------------------------------------------------
 * 用户原话（第一张图圈着「模式」「形象与素材」两个页签）：**「合为一个 step」**，
 * 紧接着指着 `/professional/products?feature=product-model` 问
 * **「为什么不引用 AI 模特的数据」**；第二张图（口播与音色）上写着
 * **「使用一下 apilio-tts 里面的接口」**。
 *
 * 三件事各自的原因不一样，逐个说：
 *
 *   1. **合并前两步**。四步里第 1 步（模式 + 用途 + 上游状态）本来就撑不满一屏 ——
 *      它没有物料、只有两个选择器，单独占一个页签，用户点进去还得再点一次。
 *      合并不是把两步的字段删掉，而是**一个页签里两张卡**：
 *      「要做哪一种」（谁出镜）与「出镜形象」（这个人的脸从哪来）。
 *      卡还是各问一个问题，页签少一个。
 *      ⚠️ 页签数变了 ⇒ `step_<page>` 的 key 必须升版本，否则老进度会落在错的页签上
 *      （老值 2 的「口播与音色」在新结构里是第 3 个，截图上完全看不出来）。
 *
 *   2. **形象图的来源要真的通向 AI 模特图**。上一版说「从素材库挑一张 AI 模特图」，
 *      而素材库是**全站混装**的：要先用别的页面把 AI 模特图「存进素材库」才会出现，
 *      没存过就是空的 —— 用户看到的是一个空库，自然要问「为什么不引用」。
 *      现在这一档直接读**「AI 模特图」档的任务产出**（`refCandidatesFor`），
 *      跑过的图一张不少地出现在候选条里，点一下就是形象图；
 *      上传人像那条路照旧保留（`source.withUpload`）—— 用户 2026-10-05 的原话是
 *      「直接引用 ai 模特**或者**上传人像」，两者是同一种输入物的两种取法。
 *
 *   3. **音色清单改走接口**。原来前端硬编码 5 条，而上游认不认只有真跑过才知道
 *      （skill 里那份 88 条的清单是逐个实测出来的）。写两份的下场是
 *      「用户在页面上选了 A、成片里的声音是 B」而所有状态全绿。
 *      现在 `useVoices()` 拉 `GET /api/aigc/voices`，下拉按语种 / 用途分组。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-06 八改：第 1、2 步**再拆开**，「分镜图与产品」独立成第 2 步（4 步）
 * ---------------------------------------------------------------------------
 * 用户原话（指着「模式与形象」页签）：
 *   **「分镜图跟产品那些还是放到第二步，step2 吧，这个还是要做成 4 步」**。
 *
 * 这一版把六改的合并**退回去**，但退的不是同一个点：
 *   · 六改合并的理由是「第 1 步只有两个选择器、撑不满一屏，单列一个页签纯属多点一次」——
 *     那时第 2 步的内容是「挑一张形象图」，是个一格的上传器，合起来确实只多占一屏；
 *   · 七改把第 2 步从「挑一张」升成了「**模特图 + 产品图 + 画面描述 → 生成分镜图
 *     → 出镜形象**」四格一条链，它自己已经撑满一屏了。这时候再和「模式」挤在一起，
 *     第 1 步就长回了 09-29 用户抱怨的那个样子（一屏 8 块）。
 *   ⇒ **合并的前提是「两边都短」；一边变长，前提就没了。** 拆开是这条前提的自然结果，
 *     不是把六改推翻 —— 页签数不是偏好，是「一屏装得下几件事」的结论。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-07 九改：第 1、2 步**再合一次**（4 步 → 3 步），选模特回到第一步
 * ---------------------------------------------------------------------------
 * 用户原话（两张候选条截图 + 一句话）：
 *   **「缩小啊，太大了，选模特放第一步吧」**（截图尺寸那半句见 `.refup__pick` 的限宽注释）。
 *
 * ⚠️ 这一版**退回了八改**，所以得把八改那句「一边变长，前提就没了」正面回答掉，
 * 否则下一个读到这里的人只会看到两条互相打架的结论：
 *
 *   · 八改拆开的理由是**当时**的第 1 步太长（一屏 8 块）；
 *   · 九改要的恰恰相反 —— 用户打开这一页，**第一件想做的事就是选模特**。
 *     他不在乎「模式」那个页签有多短，他在乎「要用的东西得先点一次才看得见」。
 *   · 所以这一版不把「模式」当一个**页签**，而是当成合并页里的**第一张卡**：
 *     页签名改叫「模式与出镜形象」，进门先看到「谁出镜」，紧跟着就能挑脸。
 *     形状与六改的合并相同（一个页签两张卡），但理由不同 ——
 *     六改是「它撑不满一屏」，九改是「**它在用户动手顺序的后面**」。
 *
 * ⇒ **「两边都短才合并」这条前提没变，变的是第 1 步必须装得下「选模特」这件事的优先级。**
 *
 * ---------------------------------------------------------------------------
 * 2026-10-07 十改：第 1 步**再拆一次**（3 步 → 4 步），只把「选模特」留在第一屏
 * ---------------------------------------------------------------------------
 * 用户原话（圈着「产品图（可选）→ 出镜形象（发给数字人）」那一整块 + 一张候选条截图）：
 *   **「这个部分放到第二step，整个流程4个step」**
 *   （同一轮的另一半是候选条「**2行就可以，其他可以滑动**」，见 `.refup__pick-row`）。
 *
 * ⚠️ 这是同一天里的第三次动页签数，看着像在来回翻，但**三次切的都是刀口位置**：
 *   · 八改切在「模式 ｜ 画面与产品」之间；
 *   · 九改把刀口挪到最上面，合成「模式 + 画面与产品」；
 *   · 十改把刀口挪到**「模特图」与「产品图」之间** ——
 *     用户要的从来不是「第 1 步短一点」，而是「**第 1 步只干挑脸这一件事**，挑完就走」。
 *   ⇒ 真正稳定的判据是**用户在这一页的动手顺序**：
 *     选模式 → 挑脸 → 配产品与画面 → 写稿 → 花钱；页签切在相邻两次动作的缝上。
 *     「一屏装得下几块」是次要判据 —— 它决定**页内**要不要折叠，不决定切在哪。
 *     （九改把它当主判据用，所以量出来的是「第 1 步变长了」，于是十改又被叫回来。）
 *
 * ---------------------------------------------------------------------------
 * 2026-10-09 十一改：4 步 → 6 步
 * ---------------------------------------------------------------------------
 * 用户原话（一张整页截图）：
 *   **「太多内容，尽量不要滑动下去，分多点step也可以，把说明啰啰嗦嗦都免了」**
 *
 * ⚠️ 这一轮**没有挪刀口**，只是把两处**本来就由两块东西组成**的步骤各自拆开：
 *   · 第 1 步「模式与模特」原本就是两张卡（要做哪一种 ｜ 出镜那张脸）；
 *   · 第 2 步「画面与产品」原本是一张卡里塞了两件事（产品与画面 ｜ 分镜图与出镜形象）。
 * 十改那段「一屏装得下几块是**次要**判据」仍然成立 —— 它决定**页内**要不要折叠。
 * 但当一张卡本身就 1540px（比左栏可视高 90%）时，切在哪已经不是审美问题了。
 *
 * 拆的依据是**量出来的**，不是感觉（`scripts/_probe-tall.py`，左栏容器 808px 起滚）：
 *   第 1 步 1294px（+486）· 第 2 步 1540px（+732）· 第 3 步 761px（不滚）· 第 4 步 1231px（+423）
 * ⇒ 4 步里 3 步要滚。拆完每页都在一屏内（复核见 `2026-10-09.md`）。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-09 十三改：6 步 → 5 步，「出镜那张脸」与「出镜形象」合并
 * ---------------------------------------------------------------------------
 * 用户原话（一张页签条截图，圈着第 2 格「出镜那张脸」与第 4 格「出镜形象」）：
 *   **「数字人这块合并一下」**。
 *
 * 他会圈这两格，是因为**它俩的名字在说同一件事** —— 第 2 步问「这张脸从哪来」，
 * 第 4 步问「最后发出去的是哪张」，中间隔着「产品与画面」。用户看到的是一条
 * 「挑脸 → … → 又是脸」的路，两格都写着「出镜」，得各点进去一次才知道差在哪。
 *
 * ⚠️ 形状与**六改/九改的合并相同**（一个页签两张卡）：不是把字段删掉，而是让
 * 「挑原料脸」与「生成分镜图 → 定下发出去的那张脸」待在同一个页签里 ——
 * 它们是**同一条链的上下游**（分镜图就是用上面那张模特图 + 产品与画面生成的）。
 *
 * 位置是问过用户才定的：**合并页留在第 2 步（「进门先挑脸」）**。代价写在明处 ——
 * 点「生成分镜图」时「产品与画面」还没填，分镜图会少一层画面依据；换来的是
 * 进门第一件事就是挑脸（十改那条「第 1 步只干挑脸这一件事」没有丢）。
 * 另两个候选（产品与画面前移 / 三页并一页）当时都摆在用户面前，他选了这一条。
 *
 * ---------------------------------------------------------------------------
 * 2026-10-09 十四改：5 步 → 4 步，「产品与画面」并进「出镜那张脸」
 * ---------------------------------------------------------------------------
 * 用户原话（一张页签条截图，圈着第 2 格「出镜那张脸」与第 3 格「产品与画面」）：
 *   **「合并一下」**。
 *
 * ⚠️ 这是十三改的**直接后果**，不是又一次改主意：十三改把「出镜形象」并进第 2 步之后，
 * 第 3 步「产品与画面」单独站着就不成立了 —— 它整页都是**可选修饰**（一个都不填照样
 * 出片），却占着一个页签；而它产出的信息（产品图 / 画面描述）恰恰是**分镜图的输入**，
 * 分镜图现在就在第 2 步里 ⇒ 两块内容分居两页，用户点「生成分镜图」时手里没有产品信息。
 *
 * ⇒ 判据还是十改那条**用户在这一页的动手顺序**：挑脸 → 配画面 → 定妆 → 写稿 → 花钱。
 *   「产品与画面」夹在「挑脸」与「定妆」之间，它本来就该和这两件事待在同一页。
 *
 * 🔴 页内顺序**不能随便调**：挑原料脸 → 产品与画面 → 生成分镜图 / 定妆。
 *   · 分镜图的提示词就是「上面那张模特图 + 产品图 + 画面描述」拼出来的；
 *   · 所以「生成分镜图」那颗按钮必须排在产品与画面**之后**。反过来做，用户会在
 *     信息没填全的时候先花一次钱（分镜图按张计费），改完描述还得再花一次。
 *
 * ⚠️ 合并后这一页 1660px（超左栏可视高一倍）⇒ 两张**可选**卡默认收起
 *    （`productOpen` / `lookOpen`）。「挑脸」是整条链上唯一的必填项，
 *    它必须留在首屏、**不许折叠** —— 折叠它等于把必填项藏起来。
 *
 * 四步的分工（一个页签回答一个问题）：
 *   ① 要做哪一种 —— 模式与口播用途
 *   ② 出镜的脸与画面 —— 挑原料脸（唯一必填）+ 产品与画面 + 分镜图定妆
 *   ③ 口播与音色 —— 平台 / 音色 / 稿子
 *   ④ 参数与生成 —— 时长 / 引擎 / 核对
 *
 * ⚠️ **克隆档仍然不过滤页签**，两档都是 4 页。十三改之后就没有「某一档少一页」
 * 这回事了 —— 克隆档只是页内不收「产品图 / 画面描述 / 分镜图」那几格
 * （`!avatar.needConsent` 条件渲染），产品名称它照样要填（AI 写稿靠它）。
 * 顺带记一条老毛病：页签过滤会把**下标与页签整体错位一格**（十一改为此把取正文从
 * 「按下标取数组」改成「按 id 取映射」，见 `PANE_BODIES`）—— 现在没有过滤了，
 * 但**别退回按下标取**，这一页的页签数还会变。
 */
const PANES: WorkflowTab[] = [
  { id: 'mode', title: '要做哪一种', desc: '先定模式与口播用途' },
  { id: 'avatar', title: '出镜的脸与画面', desc: '挑一张脸、配好产品与画面，生成分镜图后定下发出去的那张' },
  { id: 'script', title: '口播与音色', desc: '投放平台、音色与口播稿' },
  { id: 'params', title: '参数与生成', desc: '时长、引擎与核对' },
]

/**
 * AI 模特图的候选来源 —— 「引用 AI 模特」读的就是这两档跑出来的图。
 *
 * 两页各有一档产模特图的工作流（`sourcePage` 不同、`app_slug` 不同），
 * 所以查两次再合并：用户可能在「产品」页给水杯配过模特，也可能在「服饰」页
 * 给衣服配过 —— 只认其中一页，另一页的图就「明明有却选不到」，
 * 而那正是 2026-09-30 场景图参考图踩过的坑（当时是指死一档）。
 *
 * ⚠️ `app_slug` 的算法与 `pages/FashionStudio.tsx`（`slugOf`）和
 * `lib/appSlug.ts`（`appSlugFromLocation`）**必须一致**：`products` 类目前缀是
 * `product-`、`fashion` 是 `fashion-`。写错一个字，候选条永远是空的，
 * 而页面不报错（空态还会写「还没跑过 AI 模特图」—— 一句假话）。
 */
const AI_MODEL_SOURCES: { page: string; slug: string }[] = [
  { page: 'professional/products', slug: 'product-product-model' },
  { page: 'professional/fashion', slug: 'fashion-model-gen' },
]

/** 候选条最多列多少张 —— 只影响「显示几张」，不影响可挑的总量（多了也是滚着看） */
const MAX_MODEL_CANDIDATES = 24

/**
 * 分镜图任务写在哪个**页键**下（2026-10-06）。
 *
 * ⚠️ 它必须与「数字人任务」的 `sourcePage` 区分开看，两者的写法本来就不同：
 *   · 数字人任务写的是 `sourcePage: appSlug`（`digital-talking-head`）——
 *     这一页一档一个 slug，顺着档走没问题；
 *   · 分镜图是**同一页里的第二个档**，它随数字人模式（口播/克隆）漂移没有意义，
 *     所以钉一个固定页键 `digital-human`，取数时第一参数也用它
 *     （`jobsFor(BOARD_PAGE, DIGITAL_BOARD_APP_SLUG)`）。
 * 两处任何一处写成别的值，表现都是「生成完了但界面一直显示还没生成」——
 * 不报错、不告警。
 */
const BOARD_PAGE = 'digital-human'

/** 分镜图一次出图大概多久（进度条与按钮文案用它，不影响上游） */
const BOARD_ESTIMATED_MS = 40000

/**
 * 配音任务的**页键与档 slug**（2026-10-09）。
 *
 * 与分镜图那套完全同理，理由也一致：配音是**同一页里的另一个档**
 * （`/tts` 那一档的适配器与计价），它不该随数字人模式漂移，
 * 所以页键钉 `digital-human`、档钉 `tts`。
 *
 * ⚠️ 为什么页键不写 `appSlug`（`digital-talking-head`）：那样 `/tts` 页
 * 也会把它当自己的历史（`jobsFor('tts')` 按页键收窄），而数字人页的历史列表
 * 又会混进一堆只有声音没有画面的记录。两处都写同一个固定值，取数才对得上。
 */
const VOICEOVER_PAGE = 'digital-human'
const VOICEOVER_APP = 'tts'

const PLATFORM_GROUPS = [
  { label: '国内平台', list: DOMESTIC_PLATFORMS },
  { label: '海外平台', list: OVERSEAS_PLATFORMS },
]

export function DigitalHumanStudio() {
  const { value: form, patch, reset } = useDraft('digital-human', DEFAULTS)
  // ⚠️ key 带版本号：页签结构每变一次**索引含义就变一次**，老进度会静默停在错的页签上
  // （页面渲染完全正常、截图看不出来）。这一页的沿革：v5「3 拆 4」→ v6「4 并 3」→
  // v7「3 拆 4」→ v8「4 拆 6」（十一改）→ v9「6 并 5」（十三改）→ **v10「5 并 4」（十四改）**。
  // 老值 3（上一版的「口播与音色」）在新结构里会落到「参数与生成」上，看着像「跳到别处去了」。
  const [step, setStep] = useLocalStorage<number>('step_digital-human-v10', 0)
  const [sp, setSp] = useSearchParams()
  const navigate = useNavigate()
  const { cancel, jobsFor, outputsFor, clearResults, refCandidatesFor } = useJobCenter()
  // 生成成功后自动清空工作区（表单 + 上传素材 + 右栏本轮产出）—— 见 GenerateFlowOptions.workspace。
  // ⚠️ 「生成分镜图」「生成配音」两次提交带 `keepWorkspace: true`：它们是辅助产物，
  //    成功时不该把刚写好的口播稿 / 形象选择清掉。
  const flow = useGenerateFlow({ workspace: { snapshot: form, reset } })
  /** 音色清单（`GET /api/aigc/voices`）—— 拉不到会降级并在界面上如实说明 */
  const voices = useVoices()
  /**
   * 音色库抽屉。
   *
   * 🔴 与 `/tts` **同一份实现、同一份清单**（`components/VoiceLibrary`）。
   * 这一页原先是一个原生 `<Select>` 夹 88 个 `<optgroup>`：能选，但
   * 「温暖女声」和「柔和女声」差在哪只能靠选完点试听 —— 而试听要一轮往返。
   * `/tts` 那边解决过同一件事（真人头像 + 搜索 + 逐条试听），
   * 复制一份到这儿就是「同一个控件两份实现」：下次改音色库只会改一边。
   */
  const [voiceOpen, setVoiceOpen] = useState(false)
  /** 「从素材库挑一条已有配音」—— 只许挑音频（见 `pickOne` 的 kind 参数） */
  const { pickOne } = useAssetLibrary()
  const toast = useToast()
  const { normalize: normalizeModel } = useModels()

  const urlMode = sp.get('mode')
  const mode: Mode = urlMode === 'clone-avatar' || urlMode === 'talking-head' ? urlMode : form.mode
  const active = DIGITAL_HUMAN_MODES.find((m) => m.id === mode) ?? DIGITAL_HUMAN_MODES[0]
  /**
   * 形象来源是**派生值**，不是表单字段（2026-09-29 四改）。
   *
   * 由模式唯一决定：真人出镜 = 需要授权的真人素材，数字人口播 = AI 模特 / 任意人像。
   * 派生而不是存起来，就没有「模式改了、来源没跟着改」的状态 ——
   * 上一版两者都是可编辑字段，两边各写一个 useEffect 互相 patch。
   */
  const avatarSource: AvatarSource = mode === 'clone-avatar' ? 'clone' : 'ai'
  const avatar = AVATAR_SOURCES.find((a) => a.id === avatarSource) ?? AVATAR_SOURCES[0]
  /**
   * 音色：草稿里可能是旧口径的短 id，或**上一个配音引擎**的音色 ID，先归一，再查显示名。
   *
   * ⚠️ **提交载荷与提示词都必须用 `voiceId`（这个派生值），不许直接读 `form.voice`** ——
   * 草稿里存着 `female-warm` 时，`form.voice` 不是上游认的字符串，发出去顶多退化成默认音色；
   * 而 2026-10-06 起配音引擎可切（MiniMax speech 在 apilio 上整体 500，临时切
   * `gpt-4o-mini-tts`），换引擎就是**换整套音色 ID** —— 老草稿里那个 MiniMax ID
   * 在新引擎下同样不存在。两种情况的症状一样（**选了 A 出来 B，任务全绿，只有耳朵听得出来**），
   * 都由 `pickVoiceId` 回落到**当前引擎的默认音色**，且回落结果会显示在下拉里。
   */
  const voicePools = useMemo(
    () =>
      voices.mine.length
        ? [...voices.groups, { label: '我的音色', voices: voices.mine }]
        : voices.groups,
    [voices.groups, voices.mine],
  )
  const voiceId = pickVoiceId(form.voice, voicePools, voices.defaultId)
  const voiceName = voices.byId(voiceId)?.name ?? voiceId

  /**
   * `?mode= / ?use=` 深链直达某个模式与用途（数字人口播 / 真人出镜 / 讲解 / 带货）。
   *
   * 侧栏已压成一层入口（2026-09-28），整页只有「数字人」一个入口、不带 query；
   * 参数保留是为了让首页快捷入口与外部分享能落到指定状态。
   * URL 与表单保持一致、不删参数 —— 用户在页面里切换时反向写回 URL（见 switchMode / setUse）。
   */
  const wantUse = sp.get('use')
  useEffect(() => {
    const targetMode: Mode | null =
      urlMode === 'clone-avatar' || urlMode === 'talking-head' ? urlMode : null
    const targetUse = DIGITAL_HUMAN_USES.find((u) => u.id === wantUse)?.id
    if (!targetMode && !targetUse) return
    if (targetMode === mode && (!targetUse || targetUse === form.use)) return
    patch({
      ...(targetMode ? { mode: targetMode } : {}),
      ...(targetUse ? { use: targetUse } : {}),
      // 形象来源不用管了 —— 它是 mode 的派生值
    })
    setStep(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMode, wantUse])

  /**
   * 深链直进真人出镜档时的时长兜底。
   *
   * 走 `switchMode` 切档时时长会跟着换，但 `?mode=clone-avatar` 直接进来时没走过那一步，
   * 时长还是上一个模式的值 —— 而视频**按秒计价**，档位与时长不咬合会直接报错价。
   */
  useEffect(() => {
    if (mode === 'clone-avatar' && form.duration !== active.duration) {
      patch({ duration: active.duration })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const profile = getPlatform(form.platform)
  const appSlug = `digital-${mode}`

  const prompt = useMemo(
    () =>
      buildDigitalHumanPrompt({
        mode,
        use: form.use,
        avatarSource,
        shotNote: form.shotNote,
        voiceId,
        voiceName,
        script: form.script,
        productName: form.productName,
        duration: form.duration,
        aspectRatio: form.aspectRatio,
        platform: form.platform,
      }),
    [form, mode, voiceId, voiceName],
  )

  /**
   * 「引用 AI 模特」的候选图 —— 两个产模特图的档跑出来的图，新 → 旧。
   *
   * 上一版把这条路寄托在**素材库**上（「从素材库挑一张 AI 模特图」），而素材库
   * 是全站混装的：AI 模特图要先被「存进素材库」才会出现，没存过就是空的 ——
   * 用户的疑问「为什么不引用 AI 模特的数据」正是这么来的。
   * 产物本来就在库里的任务记录上，直接读它，没有中间那道手工动作。
   *
   * ⚠️ 挂 `refCandidatesFor`（不是 `outputsFor`）：前者保留 `remote`、也不受
   * 「结果栏已收起」影响 —— 上一轮跑出来的图**刷新后还在**，挑不到才是 bug。
   */
  const modelCandidates = useMemo(() => {
    const all = AI_MODEL_SOURCES.flatMap((s) => refCandidatesFor(s.page, s.slug))
    // 两页各自有序，合并后只保证不重复；截断只影响显示多少张（多了也是横向滚）
    return Array.from(new Set(all)).slice(0, MAX_MODEL_CANDIDATES)
  }, [refCandidatesFor])

  /**
   * 分镜图的取数与「刚落地的自动选中」放在**右栏取数之后**（见下面的 `jobs`）——
   * 不是为了可读性，是因为 `scripts/smoke-nav.ts` 用**第一个 `jobsFor(...)`**
   * 判定「右栏取数键」。把分镜图那处排在前面，断言读到的就成了它，
   * 于是要求右栏标题跟着变 —— 而右栏根本没动。顺序在这里是契约的一部分。
   */

  /**
   * ⚠️ 这里**不发 `resolution`**。
   * 用户也从没选过，但这个值会一路走到适配器、被翻译成上游节点 186 的 `fieldValue`
   * —— 也就是**真的按 1080 出片**。同一句话在界面上的表述却是
   * 「比例跟随形象图」，一个字都没提分辨率。
   *
   * 一个用户看不见、改不了、却真影响成片的参数，比缺一个控件糟得多：
   * 以后出片糊了，没人会想到是这里写死的。
   * 上游自己的默认（最长边 1024）就够了，哪天要让它可调，先在页面上长出控件。
   */
  const price = usePricePreview({
    appSlug,
    model: DIGITAL_HUMAN_MODEL_SLUG,
    outputs: 1,
    aspectRatio: form.aspectRatio,
    inputs: { duration: form.duration },
  })

  const jobs = jobsFor(appSlug)
  const outputs = outputsFor(appSlug)

  /**
   * 配音这一档的任务与产出（2026-10-09）。
   *
   * ⚠️ 这一处取数**必须排在 `jobsFor(appSlug)` 之后** ——
   * `scripts/smoke-nav.ts` 用文件里**第一个 `jobsFor(...)`** 判定「右栏取数键」，
   * 排到前面断言读到的就成了它，于是要求右栏标题跟着变，而右栏根本没动。
   * 分镜图那两行当初就是为同一个理由挪到后面的（见上面那段注释）。
   *
   * 用 `outputsFor`（本机本轮）而不是 `refCandidatesFor`：配音是**这一单刚合的**，
   * 它要能自动绑到「本次做口型用的音频」上；把历史里的几十条倒进选择范围，
   * 用户还得在一堆旧配音里找刚才那一条。历史/素材库那两条路走
   * 「从素材库选」的按钮。
   */
  const voJobs = jobsFor(VOICEOVER_PAGE, VOICEOVER_APP)
  const voOutputs = outputsFor(VOICEOVER_PAGE, VOICEOVER_APP)
  const voBusy = voJobs.some((j) => isActive(j.state))

  /**
   * 配音那一档的**模型 id 与报价**。
   *
   * ⚠️ 走 `DEFAULT_FAMILY_ID.audio` + `normalizeModel(..., 'audio')`，**不写死 slug**：
   * 哪一档可用由接口下发的清单决定（`/tts` 页也是这么取的）。写死一个 slug 发出去，
   * 上游认不认只能等任务回来才知道 —— 而那时钱已经花了。
   */
  const audioModelId = normalizeModel(DEFAULT_FAMILY_ID.audio, 'audio')
  const voPrice = usePricePreview({ appSlug: VOICEOVER_APP, model: audioModelId, outputs: 1 })

  /**
   * 🔴 **会被念出来的那一份** —— 稿子剥掉时间轴标记（`lib/spoken.ts`）。
   *
   * 2026-10-09 用户报障：「怎么把时间轴都跟着读了」。稿子上的 `0-3s：`
   * 是**给人看的分段标注**（分镜图与成片时长都按它对齐），不是台词 ——
   * 而配音那一格原样把整段递给了 TTS，于是「0-3s:」被念了出来。
   * 正确做法是**稿子照旧带时间轴、念的时候剥掉**，不是让 AI 别写时间轴
   * （那等于把「每段几秒」从界面上抹掉）。
   *
   * ⚠️ **派生，不存 state**：`form.script` 是唯一真源（用户看得见、改得动）。
   * 在草稿里另存一份「念的版本」，用户手改稿子时两处必然不同步，
   * 症状是「念的和他看见的不是同一份」—— 本项目最难查的那一类错。
   *
   * 取它的是这五格，**必须全都是它**；漏一处，就是一份带时间轴的台词溜进上游：
   *   ① 配音单的 `prompt` + `inputs.text`（`makeVoiceover`）；
   *   ② 成片单的 `inputs.script`（`none` / `stale` 档后端拿它去合成）；
   *   ③ 报价弹窗里展示、**且可编辑**的那段台词（`info.prompt`）——
   *      它在弹窗里改完会经 `promptInputKey: 'script'` 覆盖 ②，
   *      初值若还是带时间轴的原文，用户一个字不改也会把它覆盖回去；
   *   ④ `audioState` 的比对基准（见下）与配音条标题；
   *   ⑤ 「约 N 秒」的估算（时间轴那几个字不发音，不该算进时长）。
   */
  const spoken = spokenText(form.script)

  /**
   * 配音与口播稿的关系 —— **这一页唯一一处判「这条配音还能不能用」**。
   *
   * 四态，判据只有 `form.audio` / `form.audioText` / `spoken` 三个量
   * （`spoken` 是 `form.script` 剥掉时间轴之后的派生值，见上）：
   *   · `none`    没有配音 ⇒ 提交时按口播稿当场合成（老路径，行为完全不变）；
   *   · `match`   这条配音照的就是现在这版稿子 ⇒ 用它，上游不再合成（省一次 TTS）；
   *   · `stale`   它照的是**另一版非空稿子** ⇒ **不用它**（见下面三条口径）；
   *   · `foreign` 不知道它照哪版合的（从素材库挑的）⇒ 用它，但界面必须说清
   *               「成片念的是这段音频里的内容，不是上面那版稿子」。
   *
   * ⚠️ `stale` 是最容易做错的那一态，三条口径：
   *   · **不许照旧用旧配音** —— 成片里念上一版台词，而界面上、任务记录里、
   *     确认弹窗里写的都是新稿子。任务状态全绿，只有把成片听一遍才发现
   *     （本项目最难查的那一类错）；
   *   · **不许静默删掉它** —— 用户刚花 5 分钱合的东西凭空消失，比不能播还难解释；
   *   · 正确行为：**留着、标明过期、提交走安全的那条路**（按现在的稿子重新合成）。
   *
   * ⚠️ `foreign` 为什么**照用**：素材库里那条是用户亲手挑的，他挑的就是台词本身
   * —— 这里没有「稿子改过了」那种意外可言（`audioText` 只由本页的「生成配音」写）。
   * 至于「没稿子只有配音」也是合法的（`script` 空、`audioText` 非空时落到 `match`）：
   * 没有新版可比，那条配音就是台词。
   *
   * 🔴 **比对基准是 `spoken` 而不是 `form.script`**（2026-10-09）：
   * `audioText` 记的是**出声的那一份**，两侧口径必须一样，否则：
   *   · 用户只把 `0-3s` 改成 `0-4s`（正文一个字没动）⇒ 音频内容其实没变，
   *     拿原文比却判成 `stale`，界面喊着「对不上」让他白花 5 分钱重合一版；
   *   · 更糟的是**本次修之前合的那条**（`audioText` 里存着带时间轴的原文）
   *     会被判成「对得上」照用 —— 而它念的就是时间轴，正是用户这次报的毛病。
   * 换基准之后那一批自然落到 `stale`：界面当场说它对不上，用户重合一版就好了。
   */
  const audioState: 'none' | 'match' | 'stale' | 'foreign' = !form.audio
    ? 'none'
    : !form.audioText.trim()
      ? 'foreign'
      : form.audioText.trim() === spoken || !spoken
        ? 'match'
        : 'stale'
  /** 真正会进 `inputs.audio` 的那一条（空 = 让上游自己合成） */
  const audioForSubmit = audioState === 'match' || audioState === 'foreign' ? form.audio : ''
  /** 手里有一条配音（不管能不能用）—— 界面上「清除配音」这类动作按它显示 */
  const hasAudio = Boolean(form.audio)
  /**
   * 「生成配音」能不能点 —— 只差一件事：**有没有可念的内容**。
   *
   * ⚠️ 判的是 `spoken` 不是 `form.script`：稿子要是只剩时间轴（`0-3s：` 一行别的
   * 都没有），剥完是空串，后端会当场报「语音合成缺文本」—— 那 5 分钱不该花，
   * 按钮直接灰着、提示照旧说「先写口播稿」。
   *
   * ⚠️ 不看 `voBusy`：正在合成时按钮变灰由 `loading` 表达（那里有转圈），
   * 禁用态再叠一层只会让用户以为「这个按钮也坏了」。
   */
  const canMakeVoiceover = Boolean(spoken)

  /**
   * 试听条装的那一条 —— **就是会进 `inputs.audio` 的那一条**。
   *
   * 这一条不是「多给一个播放器」，而是这一页的**核对手段**：用户合完配音要听一遍
   * 才敢拿它去对口型（断句、多音字、语速只有耳朵能验）。
   * 所以它只认 `form.audio` —— 与载荷同源。给别的地址（比如「最新一条产出」）
   * 就会出现「试听的是 A、成片用的是 B」，正是 `AudioPlayerBar` 头注里那条报障。
   *
   * ⚠️ `nonce` 恒为 0 = **永不自动出声**：这条音频有两条来路（用户刚点了「生成配音」、
   * 或从素材库挑的），而 `AudioPlayerBar` 的自动播语义是「用户点了某条产出的播放键」。
   * 这一页没有那种键，所以按钮永远说了算（点了才响）。
   */
  const audioBar = form.audio
    ? {
        url: form.audio,
        title:
          audioState === 'foreign'
            ? '这条配音来自素材库'
            : `本次配音 · ${spoken.slice(0, 14) || '已完成'}`,
        subtitle: `${voices.engineLabel} · ${voiceName}`,
        nonce: 0,
      }
    : null

  /**
   * 分镜图这一档的任务与产出（2026-10-06 新增）。
   *
   * ⚠️ 三个取数各自的关键点：
   *   · `jobsFor(BOARD_PAGE, …)` —— 第一个参数是**页键**（`canonPageKey` 折算），
   *     这里是 `digital-human`（数字人页提交分镜图时写的 `sourcePage`）。
   *     写成 `appSlug` 会一条都查不到，而界面只会安静地显示「还没生成」。
   *   · `refCandidatesFor` 而不是 `outputsFor` —— 与上面「AI 模特图」候选同理：
   *     它保留 `remote`、也不受「结果栏已收起」影响。上一轮生成的分镜图
   *     刷新后还在库里，挑不到才是 bug。
   *   · `isActive` 从 `JobCenterContext` 导入，**不自己写 `state !== 'queued' && …`**
   *     （那是全站唯一判据，手写副本会在状态机加态时静默漏改）。
   */
  const boardJobs = jobsFor(BOARD_PAGE, DIGITAL_BOARD_APP_SLUG)
  const boardCandidates = refCandidatesFor(BOARD_PAGE, DIGITAL_BOARD_APP_SLUG)
  const boardBusy = boardJobs.some((j) => isActive(j.state))
  /**
   * 后台正在生成几张 —— 候选条上用几个「生成中」的格子占位。
   *
   * ⚠️ 取**在跑那个任务的 `expectedOutputs`**，不是 `form.boardOutputs`：
   * 用户提交完之后又去改了张数（表单是自由的）时，两者会不一致，
   * 而格子的含义是「后台这一批大概会回来几张」—— 说的是**正在发生的事**。
   */
  const boardPending = boardJobs
    .filter((j) => isActive(j.state))
    .reduce((n, j) => n + (j.expectedOutputs || 1), 0)

  /**
   * 「刚点过生成分镜图，等新的那一张落地」—— 落地后自动设为出镜形象。
   *
   * 为什么用 `useRef` 存基线而不是比数量：并发/重试时数量可能不变（旧的被清掉、
   * 新的补上），比数量会漏判；比**首张地址**才准。
   * 落地后立刻清基线，用户之后手动换形象图不会被这里覆盖回去。
   */
  const boardBaseline = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (boardBaseline.current === undefined) return
    const top = boardCandidates[0]
    if (!top || top === boardBaseline.current) return
    boardBaseline.current = undefined
    patch({ refs: [top] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardCandidates])

  /**
   * 「刚点过生成配音，等它落地」—— 落地后自动绑成做口型用的那一条。
   *
   * 形体与上面 `boardBaseline` 完全一样（比地址不比数量），只多了一件事：
   * **同时记下这条配音照的是哪一版稿子**。
   *
   * 🔴 `voScript` 存的是**点「生成配音」那一刻**的稿子，不是落地那一刻的
   * `form.script`。差别在用户合成期间又改了稿子这种情况：合成用的明明是旧稿，
   * 拿新稿去当 `audioText` 会让页面说「这条就是照现在这版合的」——
   * 于是一段对不上的台词被当成对得上的送进上游，而所有状态全绿。
   * 稿子改过之后 `audioState` 落到 `stale`，页面会当场说出来。
   *
   * ⚠️ 存的是**送给 TTS 的那一份（派生值 `spoken`）**，不是 `form.script` 原文 ——
   * `audioState` 是拿它跟 `spoken` 比对的，两侧口径必须一个字不差。
   * 存原文的话，只改时间轴不改正文也会判成 `stale`，让用户白花 5 分钱重合一版。
   */
  const voBaseline = useRef<string | undefined>(undefined)
  const voScript = useRef('')
  useEffect(() => {
    if (voBaseline.current === undefined) return
    const top = voOutputs[0]
    if (!top || top.url === voBaseline.current) return
    voBaseline.current = undefined
    patch({ audio: top.url, audioText: voScript.current })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voOutputs])

  /**
   * 最终真正发给数字人接口的那张图 —— **派生，不存 state**。
   *
   * 顺序即优先级：**先生成的分镜图**（`refs`）优于**模特原图**（`modelRefs[0]`）。
   * 没生成分镜图就直接提交是允许的（用户赶时间、或模特图本身就够用），
   * 但界面必须**如实说出此刻用的是哪一张** —— 见下面「出镜形象」卡上的 hint。
   * 存成 state 的话，「换了模特但分镜图没重生成」这种组合会让界面和载荷打架。
   */
  const effectiveRefs = form.refs.length ? form.refs : form.modelRefs.slice(0, 1)

  /** 分镜图的提示词（场景 / 动作 → 出镜画面）。见 `prompts.buildDigitalBoardPrompt` */
  const boardPrompt = useMemo(
    () =>
      buildDigitalBoardPrompt({
        productName: form.productName,
        sceneNote: form.shotNote,
        aspectRatio: form.aspectRatio,
        hasProductRef: form.productRefs.length > 0,
        hasModelRef: form.modelRefs.length > 0,
      }),
    [form.productName, form.shotNote, form.aspectRatio, form.productRefs.length, form.modelRefs.length],
  )

  /**
   * 分镜图的报价 —— 与数字人那笔**分开算**（这是两次独立的计费）。
   *
   * `model` 传的是**模型族 id**（`DIGITAL_BOARD_FAMILY`），与提交时一致：
   * `usePricePreview` 与 `JobCenterContext.submit` 都先用 `familyById` 解析它，
   * 一个传族 id、另一个传 slug 的话，两边算出来的价格可能不是同一个模型。
   */
  const boardPrice = usePricePreview({
    appSlug: DIGITAL_BOARD_APP_SLUG,
    model: DIGITAL_BOARD_FAMILY,
    outputs: form.boardOutputs,
    aspectRatio: form.aspectRatio,
    inputs: { outputs: form.boardOutputs },
  })

  /*
    生成分镜图的 `makeBoard` 排在 `doGenerate` **之后**（见下面）。
    位置在这里是契约的一部分：`scripts/smoke-nav.ts` 取本文件里**第一个 `args:`**
    作为「提交契约」来核对右栏取数键 —— 辅助动作排在主提交前面，它读到的就是
    分镜图那一份（`sourcePage: BOARD_PAGE`），于是判「右栏读 appSlug 却提交 BOARD_PAGE」。
  */

  const switchMode = (id: string) => {
    const next = DIGITAL_HUMAN_MODES.find((m) => m.id === id)
    if (!next) return
    patch({
      mode: next.id,
      duration: next.duration,
      aspectRatio: next.ratioDefault,
    })
    // URL 跟着走，否则侧栏高亮和地址会跟当前模式脱节
    setSp(next.id === 'clone-avatar' ? { mode: next.id } : { mode: next.id, use: form.use }, {
      replace: true,
    })
    setStep(0)
  }

  /** 用途与模式一样：改了就写回 URL，侧栏「数字人讲解 / 数字人带货」的高亮才对得上 */
  const setUse = (id: string) => {
    patch({ use: id as DigitalHumanUse })
    setSp({ mode: 'talking-head', use: id }, { replace: true })
  }

  /**
   * 各步的硬门槛：
   *   1. 出镜的人要有依据 —— **两档都必须有一张能发出去的图**。
   *      上游只有一个 LoadImage 节点，没图就提交不了（不是"效果差一点"，是直接 400）；
   *      真人出镜档还要额外确认授权，那是法律问题不是偏好问题。
   *   2. 口播稿必须有 —— 没有稿子就没有音频，对口型无从谈起。
   *
   * ⚠️ 判据用 `effectiveRefs`（分镜图优先、模特图兜底），**不是 `form.refs`** ——
   * 用户没生成分镜图、但选好了模特图时，用 `form.refs` 判会让「生成」按钮
   * 一直是灰的，而界面上一张脸明明摆在那里。
   */
  const avatarOk = effectiveRefs.length > 0 && (!avatar.needConsent || form.authorized)
  /**
   * 「有稿子可念」—— 判据是派生值 `spoken`，与 `canMakeVoiceover` 同一口径。
   *
   * ⚠️ 不用 `form.script.trim()`：稿子只剩时间轴时那个判据为真，但剥完是空串，
   * 提交后后端会报「语音合成缺文本」（钱与时间白花一单）。
   */
  const scriptOk = Boolean(spoken)
  /**
   * 有口播稿 **或** 有一条能用的配音，就可以提交。
   *
   * 🔴 2026-10-09 放宽。之前是「口播稿必填」，而配了「生成配音」之后
   * **稿子不再是唯一的口播来源**：用户手里那条音频就是台词。
   * 不放宽的话，从素材库挑完配音、把稿子清掉（或本来就只想用那条配音）的用户
   * 会看到一颗永远灰着的「生成」，而界面上明明写着「已选用这条配音」。
   * 后端同一处也已放开（`infinite_talk.submit` 只要「稿子或音频**至少有一个**」）。
   */
  const audioOk = Boolean(audioForSubmit)
  const canSubmit = avatarOk && (scriptOk || audioOk)

  const doGenerate = () =>
    flow.request({
      info: {
        title: active.name,
        appSlug,
        model: ENGINE_NAME,
        platform: profile.name,
        outputs: 1,
        /**
         * ⚠️ 这一行不能写 `form.aspectRatio`（一个具体比例）。
         *
         * 成片比例**由形象图决定**，上游不接受外部指定 —— 页面上的 hint 也写着
         * 「成片比例跟随形象图」。弹窗里再报一个「9:16」，就是在花钱前那一刻
         * 对着同一件事给出两个答案，而弹窗这句读起来像成品属性。
         * （`args.aspectRatio` 仍传数值：它只用于结果卡的预览框，不影响出片。）
         */
        aspectRatio: '跟随形象图',
        refImages: effectiveRefs.length,
        /**
         * ⚠️ 确认弹窗里展示的是**这段视频会被念出来的那份台词**，
         * 不是那段拼好的 `prompt` 模板。
         *
         * 这一档的上游（RunningHub InfiniteTalk / HeyGen v2v）只有
         * 「形象图 / 音频 / 动作」几个节点，`prompt` 它一个字都不读 ——
         * 把模板摊在用户面前，他看着像在核对模型输入，实际核对的是一张说明书；
         * 而真正该在掏钱前确认的那几句台词反而没露面。
         * 模板仍原样发在 `args.prompt` 里（落库 + 任务标题用），没有丢。
         *
         * 🔴 2026-10-09：台词**不一定来自这段稿子**了。配了配音之后有两支：
         *   · 用配音（`match` / `foreign`）→ 会出声的是**那段音频**，
         *     稿子在这一单里不出声。此时还写「会被念出来的口播稿」就是假话 ——
         *     用户会在弹窗里读到一段台词、确认、然后拿到一段**别的内容**的成片。
         *   · 按稿子合成（`none` / `stale`）→ 台词就是稿子，标题照旧。
         *
         * ⚠️ 这一格**在弹窗里是可编辑的**，所以 `prompt` 必须原样传一段**台词**、
         * 不许垫一句「（本单没有说话用的口播稿…）」之类的说明文字：
         * 用户随手改它一下，那句说明就会顺着 `promptInputKey: 'script'`
         * 变成真正的台词送进上游。稿子是空的就让它空着，「没有稿子」这件事
         * 由 `promptLabel` 说清楚。
         *
         * 🔴 传的是**派生值 `spoken`（剥掉时间轴的那一份），不是 `form.script`**。
         * 两个理由，缺一个都会踩坑：
         *   · 这一格的字面意思就是「会被念出来的那份台词」，而时间轴不发音 ——
         *     摆一份带 `0-3s：` 的原文，用户核对的是别的东西；
         *   · 它会**覆盖** `inputs.script`（见 `withPromptEdits` 与下面 `promptInputKey`）。
         *     初值若还是带时间轴的原文，用户在弹窗里**动一个字**（补个标点也算），
         *     整段文字就会顺着覆盖把那几个时间轴标记一起送回来。
         *     ⚠️ 只是「不改就不覆盖」救不了这一格：`changedBase` 的判据是
         *     「与初值逐字比」，初值脏 ⇒ 用户任何一次编辑都是在脏值上编辑。
         */
        prompt: spoken,
        promptLabel: audioOk
          ? spoken
            ? '本页口播稿（时间轴标记已去掉；这一单不由它出声 —— 用的是你已合好的配音）'
            : '本页没有口播稿 —— 这一单的台词就是你选的那条配音'
          : '会被念出来的口播稿（0-3s 这类时间轴标记不发音，已去掉）',
        price: price?.estimatedPrice ?? 0,
        basis: price?.basis,
        free: price?.isFreeThisCall,
      },
      args: {
        appSlug,
        title: `${active.name} · ${form.productName || '形象'}`,
        // 与 jobsFor(appSlug) 同键，见 SocialImageStudio 的同名注释
        sourcePage: appSlug,
        model: DIGITAL_HUMAN_MODEL_SLUG,
        platform: form.platform,
        prompt,
        /**
         * 🔴 「被念出来的那一格」是 `inputs.script`，**不是 `prompt`**。
         *
         * 确认弹窗里那段台词是可编辑的，改完由 `withPromptEdits` 并回载荷；
         * 不声明这一句的话，用户在弹窗里把台词改了两句、确认、
         * 钱照扣、成片念的还是**旧台词** —— 而界面上、任务记录里、弹窗里
         * 写的都是他改过的那版。这类错没有第二处能发现（`prompt` 在本档
         * 只是给人看的模板，上游一个字都不读）。
         */
        promptInputKey: 'script',
        inputs: {
          ...form,
          /**
           * 🔴 **被念出来的那一格**（`none` / `stale` 时后端拿它去合成）
           * —— 必须是派生值 `spoken`，不是 `...form` 带进来的 `form.script`。
           *
           * 2026-10-09 用户报障：稿子上的 `0-3s：` 跟着被念了出来。
           * 它在后端是「文本」这一个输入，多一个字符就多念一个字。
           *
           * ⚠️ 排 `...form` **之后**才生效（现在就在后面，别挪到前面）。
           */
          script: spoken,
          mode,
          // avatarSource 是派生值、不在 form 里，显式带上（后端 prompt 与审计都要它）
          avatarSource,
          // 音色要发**上游认的音色 ID**（`voiceId` 是翻译过旧口径的派生值），
          // 不是草稿里可能还留着的短 id。后端拿它去调 TTS 的 `voice` 字段。
          voice: voiceId,
          /**
           * 🔴 **做口型用的那段配音**（2026-10-09 加，空 = 让上游自己合成）。
           *
           * ⚠️ 必须发**派生值 `audioForSubmit`**，不许直读 `form.audio` ——
           * 那个字段可能装着一份已经过期的配音（用户改过稿子），
           * 直读就是把上一版台词送进成片，而页面上、确认弹窗里、任务记录里
           * 写的都是新稿子（本项目最难查的那一类错）。
           *
           * 后端链路：`_build_options` 折算成 `options["audio_ref"]` →
           * `infinite_talk.submit` 跳过 TTS、直接把它喂给对口型节点。
           * 折算不过去时**整单失败**，绝不静默退回「那就再合成一遍」——
           * 否则「引用了哪一条」这件事就失去意义了。
           */
          audio: audioForSubmit,
          __title: `${active.name} · ${form.productName || '形象'}`,
        },
        // 形象图 = 唯一参考图。上游只吃 1 张，多的会被适配器丢掉并记日志。
        //
        // ⚠️ 用 `effectiveRefs`：**分镜图优先，没有分镜图才回落模特图**。
        // 上一版直读 `form.refs`，而 `refs` 现在装的是「生成出来的分镜图」——
        // 用户没点生成时它是空的，任务会因为「缺少形象图」在提交期就报错，
        // 而界面上明明摆着一张他刚选好的模特图。
        imageUrls: effectiveRefs,
        aspectRatio: form.aspectRatio,
        outputs: 1,
        estimatedMs: estimateMs(form.duration),
      },
    })

  /**
   * 生成分镜图 —— 一次**独立的生图任务**（不是数字人任务的子步骤）。
   *
   * ⚠️ **排在 `doGenerate` 之后**：`scripts/smoke-nav.ts` 取本文件里第一个 `args:`
   * 作为「提交契约」来核对右栏取数键。辅助动作排在主提交前面，它读到的就是
   * 分镜图那一份（`sourcePage: BOARD_PAGE`），于是误判
   * 「右栏读 appSlug 却提交 BOARD_PAGE」。顺序在这里是契约的一部分。
   *
   * 它走与其它生成页同一套「报价确认 → 提交 → 后台轮询」流程（`useGenerateFlow`），
   * 所以用户照样在花钱前看到将发送的提示词；产出落地后由上面那个 effect
   * 自动设成出镜形象。
   *
   * ⚠️ 提交的参考图顺序是**模特图在前、产品图在后**（`imageUrls` 的先后有语义：
   * 第一张是主体）。反了的话模型会把产品当主体、把人当配件。
   */
  const makeBoard = () => {
    boardBaseline.current = boardCandidates[0] ?? ''
    flow.request({
      info: {
        title: '生成分镜图',
        appSlug: DIGITAL_BOARD_APP_SLUG,
        model: 'GPT Image 2.5',
        platform: profile.name,
        outputs: form.boardOutputs,
        aspectRatio: form.aspectRatio,
        refImages: form.modelRefs.length + form.productRefs.length,
        prompt: boardPrompt,
        price: boardPrice?.estimatedPrice ?? 0,
        basis: boardPrice?.basis,
        free: boardPrice?.isFreeThisCall,
      },
      args: {
        appSlug: DIGITAL_BOARD_APP_SLUG,
        title: `数字人分镜图 · ${form.productName || '形象'}`,
        sourcePage: BOARD_PAGE,
        model: DIGITAL_BOARD_FAMILY,
        platform: form.platform,
        prompt: boardPrompt,
        inputs: {
          ...form,
          __title: `数字人分镜图 · ${form.productName || '形象'}`,
          // 分镜图这一步实际读的两个输入，显式带上（后端 prompt 与审计都要它）
          sceneNote: form.shotNote,
          boardRefs: [...form.modelRefs, ...form.productRefs],
        },
        imageUrls: [...form.modelRefs.slice(0, 1), ...form.productRefs],
        aspectRatio: form.aspectRatio,
        outputs: form.boardOutputs,
        estimatedMs: BOARD_ESTIMATED_MS,
      },
      // 辅助产物：成功后不清工作区（口播稿与形象还要用）—— 见 GeneratePayload.keepWorkspace
      keepWorkspace: true,
    })
  }

  /**
   * 生成配音 —— **本页真跑一次语音合成**（2026-10-09 新增，用户口径：
   * 「生成配音 → 直接对口型」）。
   *
   * ## 它为什么必须是一个真任务，而不是「提交时顺带合成」
   *
   * 在这之前 TTS 是**藏在上游里面**的：用户写「0-3s：你家厨房台面还在用抹布擦水吗」，
   * 点生成，等几分钟，成片出来才知道那句话被念成了什么样（断句、多音字、语速）。
   * 而失败一次的钱是按整档收的。所以这一版把配音**提到台面上**：
   * 先在本页合出来听一遍，确认了再拿去做口型 —— 试听的与成片里的是同一条。
   *
   * ## 四条口径（都对应一处真会犯的错）
   *
   *  ① **它写在自己的页键下**（`VOICEOVER_PAGE` + `VOICEOVER_APP`），
   *     不写 `appSlug` —— 否则 `/tts` 页会把这些当自己的历史，数字人页的历史里
   *     又会混进一堆只有声音没有画面的记录（见 `VOICEOVER_PAGE` 的注释）。
   *  ② **`prompt` 就是要被念出来的全文本身**，与 `/tts` 页逐字同一口径：
   *     这一档上游只有「文本」一个输入节点，多塞一句「请合成下面这段」会被念出来，
   *     而任务照样 succeeded、钱照扣。
   *  ③ **`inputs.voice` 必须发派生值 `voiceId`**（草稿里可能还留着换引擎前的旧 ID，
   *     而 `_run_audio_phase` 对不在清单里的音色是**直接报错**，不是回落）。
   *  ④ **报价与条数恒为 1**：一次任务 = 一段稿子 = 一条 mp3（长稿由 skill 切段拼接，
   *     用户看到的仍是一条）。后端有一条硬校验「报价条数 == 文本段数」，
   *     两处不一致会当场报错而不是静默少合一条。
   *
   * ⚠️ **排在 `makeBoard` 之后**：`scripts/smoke-nav.ts` 取本文件里**第一个 `args:`**
   * 作为主提交契约来核对右栏取数键。辅助动作排到前面，断言读到的就是它。
   */
  const makeVoiceover = () => {
    /**
     * 🔴 送给 TTS 的是**派生值 `spoken`，不是 `form.script`**。
     *
     * 2026-10-09 用户报障：「怎么把时间轴都跟着读了」——
     * 稿子上的 `0-3s：` 是给人看的分段标注，原样递上去就被念了出来。
     * 这一格与 `/tts` 页是**同一条口径**（`prompt` 就是要被念的全文本身，
     * 多塞一句「请合成下面这段」也会被念出来）——正因如此，它必须是
     * **真正要出声的那一份文本**，而时间轴不在里面。
     */
    const text = spoken
    voScript.current = text
    voBaseline.current = voOutputs[0]?.url ?? ''
    flow.request({
      info: {
        title: '生成配音',
        appSlug: VOICEOVER_APP,
        model: audioModelId,
        platform: `${voices.engineLabel} · 音色 ${voiceName}`,
        outputs: 1,
        refImages: 0,
        prompt: text,
        price: voPrice?.estimatedPrice ?? 0,
        basis: voPrice?.basis,
        free: voPrice?.isFreeThisCall,
      },
      args: {
        appSlug: VOICEOVER_APP,
        title: `数字人配音 · ${text.slice(0, 16)}`,
        sourcePage: VOICEOVER_PAGE,
        model: audioModelId,
        prompt: text,
        inputs: {
          text,
          voice: voiceId,
          speed: 1,
          // 只进用量账本，便于复盘「这条音频当初是哪个引擎出的」
          engine: voices.engine,
          __title: '生成配音',
        },
        outputs: 1,
        estimatedMs: estimateAudioMs(text.length),
      },
      // 辅助产物：成功后不清工作区（这条配音还要拿去做口型）—— 见 GeneratePayload.keepWorkspace
      keepWorkspace: true,
    })
  }

  /**
   * 从素材库挑一条配音 —— 用户口径里的那半句：「存入资产库，
   * 到时候可以让 `/digital-human` 引用」。
   *
   * ⚠️ `pickOne('audio')` 的 `kind` **必须传**：素材库是全站混装的，
   * 让用户在几百张图里翻出一段 mp3，翻到之后还可能点错一张图 ——
   * 而那个错会一路走到上游（对口型节点收到一张 png）。约束落在控件里，
   * 不靠拿到结果之后再校验：那时用户已经白挑了一轮。
   *
   * ⚠️ 挑来的音频 **`audioText` 清空**（= `audioState: 'foreign'`）：
   * 我们不知道它照哪版稿子合的，编一个「就是现在这版」是假话；
   * 清空之后界面会如实说「成片以这段音频为准」（见 `audioState` 的注释）。
   */
  const pickAudioAsset = async () => {
    const item = await pickOne('audio')
    if (!item) return
    patch({ audio: item.url, audioText: '' })
    toast.success(`已选用「${item.name}」做口型，提交时上游不再自己合成`)
  }

  /* ---------------------------- 各步骤内容 ---------------------------- */

  /**
   * 按 id 取页签名 —— **页面里所有「去哪一页」的指路文案都走它**。
   *
   * 十一改之前这些文案写的是 `PANES[0]` / `PANES[1]` / `PANES[2]`，注释里
   * 反复出现「⚠️ 索引是硬编码的」—— 因为这一页的页签数改过七次，每改一次
   * 都要回来把三处下标一起挪，漏一处就出现「提示指向一个不存在的步骤」，
   * 而那行字读起来完全正常。
   * 取对象（`PANE_BODIES` 是同一套做法），改名与调序都不会再漂。
   *
   * ⚠️ 查的是 `PANES` 而**不是** `tabs`：某一档里被页内条件收起来的卡
   * （克隆档没有「出镜形象」那张卡）也可能被文案点到，查 `tabs` 会得到空串
   * ⇒ 「请在「」上传…」。⚠️ 十三改起两档的页签清单已完全一致，但这条口径
   * 仍然成立（`tabs` 只是 `PANES` 的别名，而页名必须从**全集**里找）。
   */
  const paneTitle = (id: string) => PANES.find((p) => p.id === id)?.title ?? ''

  /**
   * 第 1 步的**第一张卡** · 谁出镜（模式 + 用途 + 上游说明）。
   *
   * 2026-10-06 八改曾把它独立成一个页签，2026-10-07 九改并回第 1 步，十改后
   * 它仍是这一页进门看到的第一块 —— 这一页的页签数改了三次，**它一次也没离开过第一屏**。
   *
   * 它本身仍是**纯决策**：两个选择器 + 两句说明，没有物料 ——
   * 与紧跟着的「挑一张脸」是两类问题，所以拆成两张卡，而不是揉进一张。
   * （十改的刀口就切在这两张卡中间：模式 + 挑脸留在第 1 步，产品与画面整体去第 2 步。）
   */
  const stepMode = (
    <Card>
      <CardHead title="要做哪一种" icon="users" />
      <CardBody>
        <Field label="模式" required>
          <Chips
            value={mode}
            onChange={switchMode}
            options={DIGITAL_HUMAN_MODES.map((m) => ({ value: m.id, label: m.name }))}
          />
        </Field>
        {/*
          用途只在「AI 模特 / 任意人像」档时有意义 —— 真人出镜那档通常是老板或主理人
          本人讲自家产品，讲解还是带货由稿子决定，不额外问一遍。
        */}
        {mode === 'talking-head' ? (
          <Field label="口播用途" hint="决定稿子的结构：讲解靠信息密度，带货靠节奏">
            <Segmented
              value={form.use}
              onChange={setUse}
              options={DIGITAL_HUMAN_USES.map((u) => ({ value: u.id, label: u.name }))}
            />
          </Field>
        ) : null}
        {/*
          这段只在口播档渲染。
          克隆档下它显示的是模式自身的描述 —— 而卡头的 hint 与 chip 的 title
          已经把同一句话说过两遍了。而口播档下它讲的是**当前选中的用途**怎么影响稿子结构，
          这句话在别处没有第二份，留着。
        */}
        {mode === 'talking-head' ? (
          <div className="callout">
            <span className="text-xs muted">
              {DIGITAL_HUMAN_USES.find((u) => u.id === form.use)?.description ?? active.description}
            </span>
          </div>
        ) : null}
        {/*
          上游状态：2026-10-05 之前这里写的是「专用数字人服务尚未接入」——
          那句话当时是真的（提交会落到通用视频模型、口型不保证），但现在已经接上了，
          留着就是一句让用户不敢用的过期警告。改成如实说明用的是哪家、大概等多久。
        */}
        <div className="callout" style={{ marginTop: 8 }}>
          <span className="text-xs muted">
            <b>{ENGINE_CHANNEL}</b> · {form.duration} 秒稿约需{' '}
            {Math.round(estimateMs(form.duration) / 60000)} 分钟，可以先去别的页面干活。
          </span>
        </div>
      </CardBody>
    </Card>
  )

  /**
   * 第 2 步的**第一张卡** · 出镜的那张脸从哪来。
   *
   * ⚠️ 2026-10-09 十三改起，这一页是**三张卡**（十四改数）：本卡（挑原料脸）
   * + `stepProduct`（产品与画面）+ `stepLook`（生成分镜图 → 定下发出去的那张脸）。
   * 十三改合并的理由与位置见 `PANES` 上方那两段 —— 简言之，前两张卡与第三张卡是
   * **同一条链的上下游**（分镜图就是用本卡选的这张脸 + 产品与画面生成的）。
   *
   * ⚠️ 2026-10-07 十改后这一卡**只剩一格**：模特图（口播档）/ 真人素材（克隆档）。
   * 产品图、产品名称、画面描述整体搬去了「产品与画面」——
   * 用户口径「这个部分放到第二step」，圈的就是从「产品图」到「出镜形象」那一整块
   * （原话见 `PANES` 上方十改那段）。
   *
   * **刀口为什么切在「挑脸」与「产品与画面」之间**（而不是别的缝）：
   *   模特图是整条链上**唯一必填**的一项，也是用户进门第一件想做的事
   *   （九改的原话就是「选模特放第一步吧」）；产品图及以下全是「可选修饰」——
   *   一个都不填照样出片。⇒ **必填的留在第一屏，可选的整体往后挪一格。**
   * 十三改合并时保住了这一条：合并页留在第 2 步（用户当场选的三个排法之一），
   * 代价写在明处 —— 点「生成分镜图」时「产品与画面」还没填。
   *
   * 克隆档（真人出镜）没有「挑脸」这件事，它这一格就是真人素材本身 ——
   * 连**合规红线块**一起留在这里：那块讲的是「这张脸从哪来、有没有授权」，
   * 与「画面长什么样」不是同一件事。⚠️ 这一档**没有第二张卡**（它不生成分镜图），
   * 由 `PANE_BODIES.avatar` 里那个 `!avatar.needConsent` 收口。
   */
  const stepAvatar = (
    <Card>
      <CardHead
        title="出镜那张脸从哪来"
        icon="users"
        /* 2026-10-09 十二改：口播档那句删了 —— 「点候选条里的一张 AI 模特图，
           或上传一张正脸人像」在这一屏里**一共说了三遍**（卡片头 hint、下面
           那张 `RefUploader` 的 fieldHint、以及没有图时的 emptyHint）。
           克隆档那句留着：它讲的是**合规前提**，与「从哪来」不是同一件事。 */
        hint={avatar.needConsent ? '真人出镜需持有本人书面授权' : undefined}
      />
      <CardBody>
        {/*
          形象图 —— 唯一的必填项，两条来源都在这一个控件里：
            · 候选条 = 「AI 模特图」档跑出来的产物（点一张就设为形象图）
            · 拖拽 / 点击上传 + 素材库 = 自己拍的人像，或别处存过的图

          ⚠️ **候选条只在「数字人口播」档出现**：真人出镜那档（`avatar.needConsent`）
          不传 `source`。那一档的合规前提是**本人书面授权**，摆一排 AI 模特图
          等于给一条绕开授权的路 —— 那正是这一档要防的事。这不是漏做，是刻意的。

          `max={1}` 是**上游的硬约束**（只有一个 LoadImage 节点），不是界面偏好；
          多传的会被适配器丢掉并记日志 —— 与其让用户以为传了三张都生效，不如在这就限制住。
        */}
        {avatar.needConsent ? (
          <>
            {/*
              真人出镜档 —— **这一档没有分镜图这一步**，素材就是最终输入。
              这一档的合规前提是本人书面授权，摆一排 AI 模特图、或让模型「生成一张脸」，
              等于给一条绕开授权的路 —— 那正是这一档要防的事。刻意，不是漏做。

              ⚠️ 所以这一档「出镜那张脸」这一页**只有这一张卡**（真人素材 + 合规红线），
              没有 `stepLook` 那张（本档不生成分镜图）；再往后「产品与画面」只剩产品名称。
            */}
            <RefUploader
              wrap="none"
              label="真人形象素材"
              required
              fieldHint="正面、无遮挡、光线均匀的一张清晰人脸照（这一档不引用 AI 模特图 —— 合规前提是本人授权）"
              urls={form.refs}
              onChange={(urls) => patch({ refs: urls.slice(0, 1) })}
              max={1}
              firstBadge="出镜"
              dropLabel="拖拽或点击上传真人素材"
              dropHint="正脸 · 单人 · 光线均匀"
              emptyHint="缺少素材时无法提交。素材质量直接决定口型与表情的自然度。"
            />

            {/*
              合规红线块 —— 2026-10-07 十改从「画面与产品」搬回这里。
              它讲的是「这张脸从哪来、有没有授权」，与同一页后面的「画面长什么样」
              不是一回事（十四改把「产品与画面」也并进这一页之后，两块内容确实同屏了，
              但**语义上没有合并**：一个是合规前提，一个是画面修饰）；
              而授权勾选是**提交的硬前提**（这一档没勾就不让提交）⇒
              它必须与素材上传同处一屏：隔着一次翻页去勾选，用户会先去查「为什么不能提交」。
            */}
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid hsl(var(--warning) / .45)',
                background: 'hsl(var(--warning) / .08)',
              }}
            >
              <div className="text-xs" style={{ lineHeight: 1.9 }}>
                <b>合规红线</b>：克隆真人形象属于肖像权使用，必须同时满足
                ① 取得本人书面授权；② 仅限本店商品内容使用；③ 发布时按平台要求标注 AI 生成内容。
                <br />
                未获授权的形象一律不得克隆 —— 这是法律问题，不是偏好问题。
              </div>
              <label
                className="text-xs"
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={form.authorized}
                  onChange={(e) => patch({ authorized: e.target.checked })}
                />
                我确认已取得该形象本人的书面授权，并已归档授权文件
              </label>
            </div>
          </>
        ) : (
          <>
            {/* 模特图 —— 谁出镜。必填 1 张：对口型要一张真实的脸。整条链上唯一必填的一格，
                所以它必须留在首屏（本页另外两张卡都默认折叠，就是为了不让它被挤下去） */}
            <RefUploader
              wrap="none"
              label="模特图"
              required
              /* 2026-10-09 十二改：原来写的是「点下面候选条里的一张 AI 模特图，或上传
                 正脸人像。正脸 · 单人 · 五官清晰 —— 这张脸要拿去逐帧驱动口型」，
                 前半句与卡片头/emptyHint 重复了三遍。留下的是**只有这里才说**的
                 那半句：要求（正脸/单人/五官清晰）与原因（拿去逐帧驱动口型）。 */
              fieldHint="这张脸要拿去逐帧驱动口型 —— 正脸 · 单人 · 五官清晰"
              urls={form.modelRefs}
              onChange={(urls) => patch({ modelRefs: urls.slice(0, 1) })}
              max={1}
              firstBadge="模特"
              dropLabel="拖拽或点击上传人像"
              dropHint="正脸 · 单人 · 光线均匀"
              source={{
                name: 'AI 模特图',
                title: 'AI 模特图的产出',
                candidates: modelCandidates,
                /* 候选条 + 上传并存：同一种输入物的两条取法，不是二选一 */
                withUpload: true,
                /* 一张都没有时把人送去那一档 —— 就地生成，回来即出现在这里 */
                onGo: () => navigate('/professional/products?feature=product-model'),
              }}
              /* 十二改删掉的那句：「两条路等价：引用已经跑过的 AI 模特图，
                 或自己上传一张正面像。」—— 两条来源就摆在同一张卡里，
                 上面那格没图时这句话只是把同一件事再说一遍。 */
            />
          </>
        )}
      </CardBody>
    </Card>
  )

  /**
   * 「产品与画面」那张卡的展开态 + 折叠态用哪句话（2026-10-09 十四改）。
   *
   * 这一整张卡都是**可选修饰**（一个都不填照样出片），却是十四改合并之后
   * 最长的一段 —— 与 `stepLook` 同一套理由、同一个做法（默认收起）。
   * 「挑脸」是唯一的必填项，只有它配占首屏。
   */
  const [productOpen, setProductOpen] = useState(false)

  /** 折叠态的 hint：优先说清「在讲哪个产品」，一个都没填时如实说它不拦路 */
  const productStateText = form.productName.trim()
    ? `产品：${form.productName.trim()}`
    : form.productRefs.length
      ? `已传 ${form.productRefs.length} 张产品图`
      : '都不填也能出片'

  /**
   * 第 2 步的**第二张卡** · 讲什么产品、画面长什么样。
   *
   * 沿革：2026-10-07 十改从第 1 步整块搬来（用户原话「**这个部分放到第二step，
   * 整个流程4个step**」）—— 那句话到十四改才**字面**成立：它现在确实在第 2 步。
   * 中间绕了两圈：十一改在这一块内部切走后半段（生成分镜图 → 出镜形象，即 `stepLook`，
   * 因为整块量出来 1540px、比左栏可视高 90%）；十三改把 `stepLook` 并进挑脸那一页；
   * 十四改把这一整块也并了进来 ⇒ 页签数 4 → 6 → 5 → **4**，见 `PANES` 上方那几段。
   *
   * 🔴 它排在这一页的**中间**，不是随手放的：`stepLook`（生成分镜图）读的就是
   * 它这里的产品图与画面描述 —— 排在分镜图后面，用户会在信息没填全时先花一次钱。
   * 顺序改错**不会报错**，只会让分镜图少一层画面依据，而截图看不出来。
   *
   * 它整块都是「可选修饰」—— 一个都不填照样出片，所以**默认收起**（见 `productOpen`），
   * 与 `stepLook` 同一套理由：三张卡加起来 1660px，只有必填的挑脸卡配占首屏。
   *
   * 两档的形状：
   *   · 口播档 —— 产品图 → 产品名称 → 画面描述；
   *   · 克隆档 —— 只有产品名称。那一档**没有**产品图 / 画面描述，也没有后面的
   *     分镜图卡：不生成分镜图，素材本身就是最终输入。
   *     产品名称两档都要（AI 写稿靠它），所以它不包在条件里，只写一份。
   */
  const stepProduct = (
    <ProductInfoSection
      title="产品与画面"
      icon="image"
      open={productOpen}
      onToggle={() => setProductOpen((v) => !v)}
      collapsedHint={`${productStateText} · 点击展开`}
      /* 产品图排在名称 / 画面描述**之前**（这一块是「先给素材、再命名与描述」） */
      refsFirst
      /*
        产品图（可选）—— 用户口径：「产品不是必须选的，需要可以上传产品图」。

        ⚠️ 它**只进分镜图**，绝不直接发给对口型接口：上游只有一个 LoadImage 节点，
        多发的图会被适配器丢掉（还留一条日志）。所以这一格的 hint 必须把
        「它去哪」说清楚 —— 否则用户会以为数字人真的能看到这张产品图。
        ⚠️ **克隆档没有这一格**：那一档不生成分镜图 ⇒ 产品图没有第二个去处，
        摆出来只会让人以为它会进上游 —— 所以 `onRefsChange` 在该档不传。
      */
      refs={form.productRefs}
      onRefsChange={
        avatar.needConsent ? undefined : (urls) => patch({ productRefs: urls.slice(0, 2) })
      }
      refsLabel="产品图（可选）"
      refsHint="不填也能出片。填了会要求分镜图里的人物拿着 / 用到它（只用于生成分镜图，不会直接发给对口型接口）"
      refsMax={2}
      refsFirstBadge="产品"
      refsDropLabel="拖拽或点击上传产品图"
      refsDropHint="产品清晰 · 白底实拍都可以"
      /*
        产品名称 —— 2026-10-07 从「口播与音色」搬进这一块
        （用户口径：「**产品第二步已经有了**」，并划掉了那一格）。

        搬的理由是**同一件事不该问两遍**：产品图与产品名称原本分在两个页签里，
        用户填一项产品信息要来回切。

        ⚠️ **不标 required**（2026-10-06 用户口径：「产品不是必须选的」）：
        它仍然值得填 —— AI 写稿靠它、分镜图的提示词靠它告诉模型人物手里拿的是什么、
        合规检查按它核对宣称是否属实。所以留**提示**，去**阻断**。
        ⚠️ 它排在 `needConsent` 分叉**之外**：克隆档没有产品图与分镜图，
        但**一样要写稿** ⇒ 产品名称是这一页两档共有的那条产品信息，只写一份。
      */
      name={form.productName}
      onNameChange={(v) => patch({ productName: v })}
      nameLabel="产品名称（可选）"
      nameHint="AI 写稿与合规核对都靠它"
      /*
        画面描述 —— 归本卡。八改后它**只喂一处**：分镜图的生图提示词。

        「想改动作怎么办」在这里是**必须回答**的：重生成分镜图 —— 那一格就在
        本页下面那张「出镜形象」卡里（十四改合并后这两张卡同页，不再是「下一步」）。
        动作不是靠一段文字描述的，是分镜图里那个人本来就长那样。
        ⚠️ 克隆档不生成分镜图 ⇒ 这一格与产品图一起收起（`onDescriptionChange` 不传）。
      */
      description={form.shotNote}
      onDescriptionChange={avatar.needConsent ? undefined : (v) => patch({ shotNote: v })}
      descriptionLabel="画面描述（可选）"
      descriptionHint="拼进分镜图的提示词：场景、动作、光线。想改动作要重新生成分镜图"
      descriptionRows={3}
      descriptionPlaceholder="如：坐在明亮的客厅沙发上，手里拿着杯子，午后自然光，固定镜头，面带微笑说话"
    />
  )

  /**
   * 「出镜形象」那张卡的展开态 + 折叠态用哪句话（2026-10-09 十三改）。
   *
   * 它与「挑脸」合并成同一页之后，这一页**不做折叠**时量出来 1705px
   * （左栏容器 808px，要滚 897px）—— 而用户十一改的原话正是
   * 「太多内容，尽量不要滑动下去」。十四改「产品与画面」也并进来之后更长。
   * 「合并」与「不滚」这两条要同时成立，只剩**页内折叠**这一条路：
   * 内容不能删（分镜图是唯一额外花钱的一步），页签不能拆（拆了就违背这次的合并）。
   *
   * 默认**收起**，由折叠头的 hint 说清「现在用的是哪张脸」——
   * 收起来之后下面那一格的 `fieldHint` 也一起看不见了，hint 是唯一的线索。
   *
   * 实测（`_probe-tall.py`，十四改后）：两张可选卡各收起成一个 76px 的折叠头 ⇒
   * 整页 **990px**、超容器 **182px**，而必填的挑脸卡本身就 682px。
   * 也就是说超出的那一截**来自必填项本身**，不是折叠没做好 ——
   * 要再压只能动挑脸卡，而它**不能折叠**（把必填项藏起来是更糟的事）。
   */
  const [lookOpen, setLookOpen] = useState(false)

  /** 折叠态的 hint 内容 —— 三态各自回答「现在发出去的是哪张脸」 */
  const lookStateText = form.refs.length
    ? '已用生成的分镜图'
    : form.modelRefs.length
      ? '还用着上面的模特图'
      : '还没定'

  /**
   * 第 2 步的**第三张卡** · 出镜形象 —— 生成分镜图，并定下**真正发给数字人的那一张**。
   *
   * ⚠️ 2026-10-09 十三改：它原来是独立的第 4 页「出镜形象」，先并入第 2 步（挑脸）
   * 当第二张卡；十四改「产品与画面」也并进来之后，它成了**第三张**。
   * **变量名保留 `stepLook`**：它是「出镜形象」这件事的实现，
   * 改名的收益抵不上多一处漂移点（`PANE_BODIES` 与冒烟都按这个名字找它）。
   *
   * 🔴 它必须排在 `stepProduct` **后面** —— 生成分镜图的提示词就是
   * 「上面的模特图 + 产品图 + 画面描述」拼出来的。顺序反了不报错，
   * 只是用户会在信息没填全时先花钱，改完描述还得再花一次。
   *
   * 十一改从「画面与产品」那张卡里切出来的后半段。它**是唯一会额外花钱的一步**
   * （分镜图按张计费，与成片那笔分开算），而且产出的那张图会盖掉上面选的模特图
   * （见 `effectiveRefs`）——「我到底发的是哪张脸」这个问题，只在有分镜图之后才存在。
   *
   * ⚠️ 真人出镜档**没有这张卡**（由 `PANE_BODIES.avatar` 里的
   * `!avatar.needConsent` 条件渲染收口，不再是「过滤掉一个页签」），
   * 所以这里不再分叉 `avatar.needConsent` —— 渲染到这一步就一定不是克隆档。
   */
  const stepLook = (
    <Card className={lookOpen ? '' : 'card--folded'}>
      <CardHead
        title="出镜形象"
        icon="users"
        /* 2026-10-09 十二改：卡片头那句「先出一张分镜图，再挑一张作为发给数字人的形象」
           删了 —— 页签描述、下面那一格的 fieldHint、以及没有图时的空态各说了一遍同一件事。
           （十三改：与「挑脸」合并后这一页 1705px 超过一屏 ⇒ 默认收起，见 `lookOpen` 的注释） */
        open={lookOpen}
        onToggle={() => setLookOpen((v) => !v)}
        hint={lookOpen ? undefined : `${lookStateText} · 点击展开`}
      />
      {lookOpen ? (
        <CardBody>

        {/*
          生成分镜图 —— 这一步的三个参数（张数 / 比例 / 参考图）都是
          **花钱前必须看得见、改得了**的：各差一档，报价就差一倍。

          ⚠️ 上一版一个入口都没有：张数写死在代码里、比例只在报价弹窗里
          露一次面。用户的原话是「**怎么4张的**」「**比例也没有**」——
          他在掏钱那一刻才发现自己不知道在买什么、也没得选。
        */}
        <Field
          label="分镜图"
          hint={`出 ${form.boardOutputs} 张候选，挑一张作为出镜形象（按张计费，与成片那笔分开算）`}
        >
          <div
            className="row"
            style={{ gap: 18, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}
          >
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <span className="text-xs muted">出几张</span>
              <Segmented
                value={String(form.boardOutputs)}
                onChange={(v) => patch({ boardOutputs: Number(v) })}
                options={BOARD_OUTPUT_OPTIONS.map((n) => ({ value: String(n), label: `${n} 张` }))}
              />
            </div>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <span className="text-xs muted">比例</span>
              <Segmented
                value={form.aspectRatio}
                onChange={(v) => patch({ aspectRatio: v })}
                options={BOARD_RATIOS.map((r) => ({ value: r, label: r }))}
              />
            </div>
          </div>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <Button
              variant="primary"
              icon="sparkles"
              loading={boardBusy}
              disabled={!form.modelRefs.length}
              onClick={makeBoard}
            >
              {boardBusy ? '正在生成分镜图…' : `生成分镜图（${form.boardOutputs} 张）`}
            </Button>
            {/*
              生成中那句必须说出**它是异步的**。

              上一版这里写的是「生成后可在下面候选条里换一张」—— 一句静态说明，
              在整个 40 秒里一个字都不变，唯一能看出「在跑」的只有按钮上那个转圈。
              用户的原话就是「**分镜生成为什么不是异步的**」：它其实是异步的
              （后端立即返回 jobId，出图在后台线程池），但**界面上没有任何
              「后台在跑」的证据** —— 转圈读起来更像「卡住了」。
            */}
            <span className="text-xs muted">
              {!form.modelRefs.length
                ? '先选一张模特图才能生成'
                : boardBusy
                  ? '已提交到后台，出图后自动设为出镜形象 —— 可以切到别的页签继续做别的'
                  : '生成后可在下面候选条里换一张'}
            </span>
          </div>
        </Field>

        {/*
          出镜形象 —— **数字人接口真正吃的那一张**，所以它排在最后。
          空着不算错：此时会自动回落到上面的模特图（见 `effectiveRefs`），
          但这件事必须在界面上说出来，不能让用户猜「我到底发的是哪张」。
        */}
        <RefUploader
          wrap="none"
          label="出镜形象（发给数字人）"
          required
          fieldHint={
            form.refs.length
              ? '当前用的是生成的分镜图。想换：点下面候选条里的另一张，或清空回落成模特图'
              : form.modelRefs.length
                ? '还没有分镜图 —— 这一步会直接用上面的模特图出片。生成分镜图后自动换成它'
                : '先选一张模特图'
          }
          urls={form.refs}
          onChange={(urls) => patch({ refs: urls.slice(0, 1) })}
          max={1}
          firstBadge="出镜"
          dropLabel="拖拽或点击上传"
          dropHint="正脸 · 单人 · 眼睛看向镜头"
          source={{
            name: '分镜图',
            title: '生成过的分镜图',
            candidates: boardCandidates,
            /*
             * 正在生成时占几个格子 —— 这是**「它在后台跑」在界面上唯一的位置**。
             *
             * 分镜图任务写的是 `sourcePage: digital-human` / `appSlug: digital-board`，
             * 而右栏取的是本页 appSlug（`digital-talking-head`）——
             * 所以这个任务**在任何任务列表里都看不到**（那是刻意的收窄，
             * 见 `jobsFor` 的注释），进度只可能落在这一处。
             */
            pending: boardPending,
            withUpload: true,
          }}
          emptyHint="留空时用上面的模特图作为出镜形象。"
        />
      </CardBody>
      ) : null}
    </Card>
  )

  /**
   * 「AI 写一稿」不让点的原因（有值 = 禁用，不隐藏）。
   *
   * 前置条件只有**产品名**一条。这一页的产品名确实可以不填（手写稿的人不需要它），
   * 但**让 AI 写稿必须有** —— LLM 看不了参考图也看不了形象，没有产品名它写出来的稿子
   * 会像真的（「这款产品采用先进工艺」），用户一路念到成片才发现卖的是空气。
   *
   * ⚠️ 2026-10-05：用户报「AI 写一稿不可用」。根因**不是**接口坏了，
   * 是它排在「口播稿」**下面**、还标着「可选」—— 用户从没见过那个字段，
   * 只看到一颗灰按钮，自然会以为功能坏了。两处改：
   *   ① 产品名移到口播稿**之前**（先给产品再写稿，顺序即因果）；
   *   ② 禁用原因同时写进字段 hint（常显），不再只挂在按钮的 hover title 上。
   */
  /**
   * 「AI 写一稿」为什么不能点。
   *
   * ⚠️ **指路必须带页签名**（产品名称与「AI 写一稿」不在同一页）：
   * 用户在「口播与音色」页签上看到「先填产品名称」，而那一格**不在本页** ——
   * 只说「先填产品名称」，他会在这页上上下下找一个不存在的输入框。
   * 页签名从 `PANES` 取，改名自动跟着走（同 `disabledReason` 的口径）。
   *
   * ⚠️ 2026-10-09 十一改起**不再写下标**：`PANES[1]` → `paneTitle('avatar')`。
   * 在此之前这一处改过三次（九改 `PANES[0]` → 十改 `PANES[1]` → 十一改又变），
   * 每改一次页签数就得回来挪一遍 —— 漏了的症状是「提示指向一个不存在的步骤」，
   * 而那行字看起来完全正常。按 id 取之后，改名与调序都不需要回来动它。
   *
   * （十四改把「产品与画面」并进「出镜的脸与画面」之后，这一处的 id 仍是 `avatar`
   * —— 页签名变了、id 没变，这正是按 id 取的好处：`paneTitle` 自动跟到新名字。）
   */
  const scriptFillBlocked = !form.productName.trim()
    ? `先到「${paneTitle('avatar')}」填产品名称 —— AI 需要知道在讲什么产品才写得出稿子`
    : undefined

  const stepScript = (
    <Card>
      <CardHead title="口播与音色" icon="video" />
      <CardBody>
        <Field label="投放平台">
          <Select
            value={form.platform}
            onChange={(e) => {
              const next = getPlatform(e.target.value)
              patch({ platform: next.id, aspectRatio: next.sizes.video })
            }}
          >
            {PLATFORM_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.list.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
        <div className="text-xs muted" style={{ margin: '-4px 0 14px', lineHeight: 1.7 }}>
          禁词与合规按 {profile.name} 走 ·{' '}
          {form.refs.length
            ? `成片比例跟随形象图 —— 当前用的是你生成的分镜图，比例 ${form.aspectRatio}`
            : '成片比例跟随形象图 —— 现在还没生成分镜图，用的是模特原图，比例由它决定'}
        </div>

        {/*
          音色 —— 清单来自 `GET /api/aigc/voices`，按语种 / 用途分组，
          并**随配音引擎一起下发**（默认引擎 303 条；2026-10-06 曾因 apilio 的
          MiniMax 渠道整体 500 临时切 `openai`，那一套是另外十几条）。
          清单与引擎必须由同一个接口一起给：模型一路、音色一路地写，
          就会出现「下拉列 MiniMax 音色、实际发 OpenAI 模型」。

          🔴 2026-10-09：**控件换成音色胶囊 + 音色库抽屉**，与 `/tts` 同一份实现
          （`components/VoiceLibrary`）。原先这里是原生 `<Select>` 夹 88 个
          `<optgroup>` —— 能选，但「温暖女声」和「柔和女声」差在哪只能靠选完点试听，
          而试听要一轮往返。`/tts` 那边解决过同一件事（真人头像 + 搜索 + 逐条试听），
          两处各留一份就是「同一个控件两份实现」：下次改音色库只会改一边。

          ⚠️ **它现在长在「配音」那一格里**（原来是独立的一格 + 一个试听按钮）：
          音色是那段配音的属性，不是独立的一项。挂在配音那一格上之后，
          「用谁的声音 → 合出来听 → 确认」是一条线上的三个动作，不用来回看两处；
          而且抽屉里自带试听，页面上原来那颗 `VoicePreviewButton` 因此下线 ——
          同一件事（试听某个音色）留两个入口，迟早会漂成两种行为。

          hint 里把**真会发出去的那一份**报出来（引擎名 + 上游音色 ID）——
          铁律「界面必须报真正会发出去的那一份」：抽屉里显示中文名（「温暖女孩」），
          上游收到的是 `Chinese (Mandarin)_Warm_Girl`；只显示中文名的话，
          用户拿这个页面跟后端日志对不上账，也看不出引擎被临时换过。
        */}
        <Field
          label="口播稿"
          required={!audioOk}
          hint={
            <span style={{ display: 'grid', gap: 4 }}>
              <span>
                按时间轴分段写，一段一个信息点；不许写产品资料里没有的功效
              </span>
              {/*
                这一句是「同一份事实只表达一次」的那一次：稿子上写着 `0-3s：`，
                而合成配音时它**不发音** —— 界面必须自己说出来。
                不说的话，用户听到的开头没有时间轴、看到的稿子有，只会得出
                「它偷偷改了我的台词」或者「这段音频是别的稿子的」两种结论（都不对）。
                ⚠️ 别把「剥掉时间轴」做成静默行为 —— 这就是它唯一的告知处。
              */}
              <span className="muted">
                时间轴标记（如 0-3s：）只用来分段，合成配音时<b>不会念出来</b>
              </span>
              {/* 禁用原因常显在字段上 —— 不再只挂在按钮的 hover 上（见 scriptFillBlocked 注释） */}
              {scriptFillBlocked ? (
                <span style={{ color: 'hsl(var(--warning))' }}>{scriptFillBlocked}</span>
              ) : null}
            </span>
          }
          right={
            /* 按钮就挂在它要填的那一格上 —— 这一页只有「口播稿」一个 AI 产出，
               放卡头反而离得远。全站唯一一份实现见 `components/AiFillButton`。 */
            <AiFillButton<FormState>
              kind="human"
              fields={FILL_FORM_FIELDS.human}
              brief={buildHumanFillBrief({
                productName: form.productName,
                script: form.script,
                use: form.use,
                duration: form.duration,
                platform: form.platform,
                shotNote: form.shotNote,
              })}
              sourcePage="digital-human"
              /* 快照必须在**覆盖前**取 */
              snapshot={{ script: form.script }}
              onFill={patch}
              blocked={scriptFillBlocked}
              label="AI 写一稿"
              busyLabel="AI 正在写稿…"
            />
          }
        >
          <Textarea
            value={form.script}
            rows={7}
            placeholder={
              '0-3s：你家厨房台面还在用抹布擦水吗？\n3-8s：（展示产品）这块硅藻土垫，水倒上去半秒就没了\n8-15s：可折叠收纳，不用的时候立起来不占地\n15s：点下面链接，今天下单送同款杯垫'
            }
            onChange={(e) => patch({ script: e.target.value })}
          />
        </Field>

        {/*
          配音 —— 2026-10-09 新增（用户口径：「生成配音 → 直接对口型」）。

          它排在口播稿**之后**，因为稿子是它的输入（顺序即因果）。它的三个动作
          也按这条线排：先选音色（抽屉）→ 合出来听（试听条）→ 不够用就从素材库换一条。

          ⚠️ 这一格**不是重复的播放器**：它是这一单做口型真正会用的那一条音频。
          「合出来听一遍」是用户唯一能验断句/多音字/语速的地方，而成片按音频对齐 ——
          所以在花钱之前必须能听到**同一条**（试听的与成片里的逐字一致）。
        */}
        <Field
          label="配音"
          hint={
            <span style={{ display: 'grid', gap: 4 }}>
              {audioState === 'none' ? (
                <span>
                  还没有配音 —— 提交时按上面的口播稿现场合成（改动稿子之后也会走这条路）
                </span>
              ) : audioState === 'match' ? (
                <span>这条配音照的就是上面这版稿子 —— 提交时上游不再自己合成，省一次语音合成的钱</span>
              ) : audioState === 'foreign' ? (
                <span style={{ color: 'hsl(var(--warning))' }}>
                  这条配音来自素材库，<b>台词以它为准</b>：成片会念它里面的内容，不是上面那版稿子
                </span>
              ) : (
                <span style={{ color: 'hsl(var(--warning))' }}>
                  这条配音<b>对不上现在这版稿子</b>了（改过稿子、或它念的是早先那个口径）——
                  直接提交会按现在的稿子重新合成（这条不会消失，想留它就点「生成配音」重做一版）
                </span>
              )}
              {voices.error ? (
                <span>音色清单没读到（{voices.error}），先用内置的常用几个 —— 刷新页面会重试</span>
              ) : (
                <span className="muted">
                  {voices.engineLabel} · 上游音色 ID：{voiceId}
                </span>
              )}
            </span>
          }
        >
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* 音色胶囊 —— 打开音色库抽屉。与 `/tts` 同一个类名、同一份实现（见上面那段注释） */}
            <button
              type="button"
              className="tts-chip"
              onClick={() => setVoiceOpen(true)}
              title="打开音色库（可搜索、可当场试听）"
              data-dh-voice="1"
            >
              <span className="tts-chip__k">音色</span>
              <span className="tts-chip__v">{voiceName}</span>
              <Icon name="chevron" size={14} />
            </button>

            <Button
              variant="primary"
              icon="sparkles"
              loading={voBusy}
              disabled={!canMakeVoiceover}
              onClick={makeVoiceover}
              title={canMakeVoiceover ? undefined : '先写口播稿 —— 配音合的就是它'}
              data-dh-voiceover="1"
            >
              {voBusy
                ? '正在合成配音…'
                : hasAudio
                  ? `重新生成配音（¥${(voPrice?.estimatedPrice ?? 0).toFixed(2)}）`
                  : `生成配音（¥${(voPrice?.estimatedPrice ?? 0).toFixed(2)}）`}
            </Button>

            <Button variant="ghost" icon="folder" onClick={() => void pickAudioAsset()}>
              从素材库选
            </Button>
            {hasAudio ? (
              <Button
                variant="ghost"
                icon="trash"
                title="只是不再用它 —— 音频本身仍在素材库里"
                onClick={() => patch({ audio: '', audioText: '' })}
              >
                不用这条
              </Button>
            ) : null}
            <span className="grow" />
            {/*
              在跑的那一句必须写出来：配音任务写在自己的页键下
              （`VOICEOVER_PAGE` + `VOICEOVER_APP`），**在任何任务列表里都看不到**
              （右栏取的是本页 `appSlug`，那是刻意的收窄）。所以「它在后台跑」
              在界面上只有这一个位置 —— 少了它，用户点完按钮只会看到一个转圈。
            */}
            <span className="text-xs muted">
              {!canMakeVoiceover
                ? '先写口播稿'
                : voBusy
                  ? '已提交到后台，合好会自动装上试听条 —— 不用等在这里'
                  : spoken
                    ? `约 ${Math.max(1, Math.round(spoken.length / 5))} 秒的语音，几秒钟就回来`
                    : ''}
            </span>
          </div>
        </Field>

        {/*
          试听条 —— 装的就是**会进载荷的那一条**（见 `audioBar`）。
          没有可播的东西时整条不渲染：空着就是一个永远点不动的播放键。
        */}
        {audioBar ? (
          <AudioPlayerBar
            track={audioBar}
            title={audioBar.title}
            subtitle={audioBar.subtitle}
            playToken={audioBar.nonce}
          />
        ) : null}
      </CardBody>
    </Card>
  )

  /* ------------------------- 第 4 步 · 参数与生成 ------------------------- */

  /**
   * 2026-10-05：这一页只剩「时长」一个控件。
   *
   * 撤掉的两件（都是界面在说假话）：
   *   · **比例** —— 成片比例跟随形象图，**数字人这一步**的上游不接受指定。
   *     留一个选了不生效的下拉，用户下次就会怀疑所有控件。
   *     ⚠️ 2026-10-07：分镜图那一步**补上了**比例选择器（生图接受指定），
   *     所以下面那句说明现在读 `form.aspectRatio`，不再报平台的默认值 ——
   *     两处口径必须一致，否则用户在分镜图选了 16:9，这一行还在说 9:16；
   *   · **模型下拉** —— 数字人只有一条上游，`ModelSelect` 里放一条等于没有选择，
   *     而它还会把「视频页那一堆档」的语境带进来。改成一行说明：用的是谁、什么通道。
   */
  const stepParams = (
    <Card>
      {/* 2026-10-09 十二改：卡片头的 `hint` 与「引擎」那一格的 `hint` 都删了 ——
          两句说的都是下面已经写着/画着的东西（「按秒计价」在右栏报价里就是
          `0.10 元/秒`；「形象图 + 配音 → 口型驱动」就是那一行字本身）。
          这一页各步「尽量不滚动」的第一刀切在**重复**上，不是切信息。 */}
      <CardHead title="时长与引擎" icon="bolt" />
      <CardBody>
        <Field
          label="时长"
          hint={`这个时长是给稿子定长的，成片长度由配音实际长度决定（可敲 ${active.durationMin}-${active.durationMax} 之间的任意秒数）`}
        >
          {/* 2026-10-09 用户口径「这个也是（自定义）」：档位旁常驻数字框，
              常用档一键选、不常用的直接敲（`DurationInput`，与视频页同一份实现）。 */}
          <DurationInput
            value={form.duration}
            onChange={(n) => patch({ duration: n })}
            presets={active.durationOptions.map((d) => ({ value: d, label: `${d} 秒` }))}
            min={active.durationMin}
            max={active.durationMax}
          />
        </Field>

        <Field label="引擎">
          <div className="row" style={{ gap: 8 }}>
            <span className="text-sm">{ENGINE_NAME}</span>
            <Badge tone="success" icon="link">
              {ENGINE_CHANNEL}
            </Badge>
          </div>
        </Field>
      </CardBody>
    </Card>
  )

  /* 镜头节奏卡的展开态 —— 默认收起（理由见那张卡上方的注释） */
  const [rhythmOpen, setRhythmOpen] = useState(false)

  const stepReview = (
    <>
      <Card>
        <CardHead title="参数核对" icon="check" />
        <CardBody tight>
          {/*
            🔴 2026-10-09 十二改：**9 行 → 4 行**。

            原来把这一步「能想起来的东西」全列了一遍 —— 模式 / 形象 / 音色 /
            口播稿 / 出镜形象 / 产品图 / 投放 / 规格 / 引擎。其中五样
            **在同一条链的其他页签上刚刚看过、而且这一屏抬头就能看见**：
              模式 → 第 1 步；形象 → 第 2 步；产品图 → 第 2 步
              （十二改这里写的是「第 3 步」，十四改把「产品与画面」并进了第 2 步）；
              规格（时长）→ 正上方那张卡；引擎 → 正上方那张卡。
            用户的原话是这一步「太多内容，尽量不要滑动下去」——
            这份 484px 的清点表就是这一步超出去的那 224px 的主因。

            留下的四行是**发出去的那几样**（形象图 + 音色 + 稿子 + 投放），
            也就是「改错了要重跑、且别处看不到结果」的四项。
            判据不是「重要不重要」，而是**这一行有没有别的地方能代替它**。
          */}
          <div className="review-list">
            <div className="review-list__row">
              <span className="review-list__k">音色</span>
              <span className="review-list__v">
                {voiceName}
                {/* 展示名之外把上游 ID 也带上：这一行是花钱前最后一眼，
                    用户拿它对得上后端日志（也才能发现「我以为选的是这个」）。 */}
                {voices.byId(voiceId) ? <span className="muted"> · {voiceId}</span> : null}
                {/* 引擎也报出来：临时切过引擎，这一行是唯一能在花钱前看见的地方 */}
                <span className="muted"> · {voices.engineLabel}</span>
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">口播稿</span>
              <span className={`review-list__v${form.script.trim() ? '' : ' review-list__v--muted'}`}>
                {form.script.trim() ? `${form.script.trim().length} 字` : '未填写'}
              </span>
            </div>
            {/*
              「出镜形象」这一行必须**说清用的是哪一张**（分镜图 / 模特原图）——
              这是花钱前最后一眼，而两种情况下发出去的画面差别很大。
              只写「已就绪」等于把用户最该核对的那件事藏起来。
            */}
            <div className="review-list__row">
              <span className="review-list__k">出镜形象</span>
              <span className={`review-list__v${effectiveRefs.length ? '' : ' review-list__v--muted'}`}>
                {!effectiveRefs.length
                  ? '未提供'
                  : form.refs.length
                    ? '生成的分镜图'
                    : '模特图（未生成分镜图）'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">投放</span>
              <span className="review-list__v">
                {profile.name}
                {/* 「比例跟随形象图」原本是单独一行「规格」，并到投放这一行 ——
                    它是**解释为什么这一页没有比例控件**的一句话，不是一项参数。 */}
                <span className="muted"> · {form.duration} 秒稿，比例跟随形象图</span>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/*
        镜头节奏 —— **默认收起**（2026-10-09 十一改）。

        它是一份**参考资料**（四段式该怎么写），不是这一步要填的东西：
        「参数与生成」这一步的动作只有「核对 + 提交」，而这张卡 305px
        正好是这一步超出可视高度的那一截（实测这一步 1281px，左栏可视 808px）。
        折起来留一行折叠头，要点开随时点得开。

        ⚠️ 折的判据仍是那条老规矩：**折起来用户就找不到的东西不能折** ——
        这张卡里没有动作（没有按钮、没有输入），只有一段文字，所以安全。
        反例见 `VideoStudio` 的分镜提示词：那颗「AI 细化镜头」当时就是因为
        被折进去而没人找得到，后来专门挪到折叠区外。

        收起态只留 `CardHead` 一行；展开区用 `.inlinefold` 把 CardHead 的默认
        内距收掉（与 `VideoStudio` 同一套，别自己拼一个 `<summary>`）。
      */}
      <Card>
        <div className="inlinefold">
        <CardHead
          title="镜头节奏"
          icon="film"
          open={rhythmOpen}
          onToggle={() => setRhythmOpen((v) => !v)}
          right={<Badge tone="primary">口型与语音同步</Badge>}
        />
        {rhythmOpen ? (
          <CardBody tight>
            <ol className="structure">
              {active.structure.map((s, i) => (
                <li key={`${i}-${s}`} className="structure__item">
                  <span className="structure__idx">{i + 1}</span>
                  <span className="grow">{s}</span>
                </li>
              ))}
            </ol>
          </CardBody>
          ) : null}
        </div>
      </Card>
    </>
  )

  /**
   * **实际渲染的页签清单** —— 两档都是 4 页（2026-10-09 十四改起）。
   *
   * ⚠️ 这里原来写的是 `avatar.needConsent ? PANES.filter((p) => p.id !== 'look') : PANES`：
   * 「出镜形象」当时是一整页、只在口播档存在，克隆档不生成分镜图 ⇒ 那一页会是
   * 一个只写着「这一档不走分镜图」的空页签。十三改把它降级成合并页（`avatar`）
   * 里的**第二张卡**，过滤随之整条撤销 —— **不再有两档页签数不同这回事**。
   * 十四改把「产品与画面」也并了进来，形态不变（还是页内条件渲染，不是过滤页签）。
   *
   * ⚠️ `step` 是持久化的旧值，可能比 `tabs.length - 1` 大（上一版停在最后一页，
   * 这一版页签少了）—— 所以取正文一律走 `Math.min` 夹一下。变量留着也是为它。
   */
  const tabs = PANES

  /**
   * 页签 id → 正文。**键集合必须与 `PANES` 的 id 集合逐项相等**（冒烟钉着这条，
   * 见 smoke-nav 的「正文映射 == 页签清单」段）—— 少一个键，点过去就是一片空白。
   *
   * ⚠️ 2026-10-09 十一改由「按下标取数组」改成「按 id 取映射」：
   * 克隆档过滤掉一页之后，下标与页签**整体错位一格** —— 点「出镜那张脸」渲染出
   * 「产品与画面」，编译期查不出来，页面也不报错。映射形态下错位根本不可能发生。
   *
   * ⚠️ 十四改之后 `avatar` 这一页是**三张卡**（挑脸 + 产品与画面 + 出镜形象），
   * 形态与 `params` 那一页相同（都是 `<>` 包多块）。**顺序不许调**：
   * 分镜图（`stepLook`）用的就是前两张卡的输入（那张模特图 + 产品图 + 画面描述），
   * 把 `stepProduct` 排到 `stepLook` 后面 ⇒ 用户会在信息没填全时先花钱生成分镜图。
   * 克隆档只收第一张卡与产品名称 —— 页内条件渲染，两档页签清单完全一致。
   */
  const PANE_BODIES: Record<string, ReactNode> = {
    mode: stepMode,
    avatar: (
      <>
        {stepAvatar}
        {/*
          顺序 = 依赖顺序：产品与画面是分镜图的输入，必须在它前面。
          `stepProduct` 在克隆档会自己收掉产品图与画面描述（只留产品名称）。
        */}
        {stepProduct}
        {/*
          克隆档没有分镜图，也就没有「出镜形象」这张卡 —— 页内不收，不是少给一页。
          那一档的素材（本人授权的真人素材）本身就是最终输入。
        */}
        {!avatar.needConsent && stepLook}
      </>
    ),
    script: stepScript,
    params: (
      <>
        {stepParams}
        {stepReview}
      </>
    ),
  }

  const cur = tabs[Math.min(step, tabs.length - 1)]
  const stepBody = PANE_BODIES[cur?.id] ?? null

  /**
   * 提交栏 —— 放在**右栏顶部**，不再挂在页签底部（2026-09-29 四改）。
   *
   * 用户原话：「下面这个放到右上角吧」。
   *   · 左栏因此只剩表单 —— 这一页各步的内容本来都撑不满一屏，
   *     底部再压一个「价格 + 大按钮 + 草稿状态」的块，它就成了视野里最重的东西；
   *   · 右栏本来就是「操作与结果」，报价与生成按钮放它顶部最顺手，
   *     且 `.studio__dock` 吸顶 —— 右栏比左栏长时（结果 + 本页历史）也不会跟着滚走。
   *
   * ⚠️ 2026-10-08 **全站统一了**：这一页不再特殊，9 个生成页的提交栏都在右栏顶部
   * （`WorkflowTabs` 的 `footer` 槽因此整个下线，见 components/WorkflowTabs.tsx）。
   * 也就是说，下面这段位置说明现在是**通用口径**，不再是这一页的例外。
   */
  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          disabled={!canSubmit}
          disabledReason={
            /* ⚠️ 页签名从 `PANES` 取，**不写死**（2026-10-06 八改）。
               这一页的页签改过六次名（形象与素材 → 模式与形象 → 模式 →
               出镜形象与产品 → 模式与出镜形象 → 模式与模特），每改一次就得回来改这几句 ——
               漏一次的症状是提示指向一个不存在的步骤，而那行字看起来完全正常，
               用户照着找一圈。
               ⚠️ 2026-10-09 十一改起按 **id** 取（`paneTitle('avatar')` / `paneTitle('script')`），
               不再写 `PANES[i]` —— 这一页的页签数改过七次，下标每改必漂。
               （`/social-image` 已按同一口径改过。） */
            !avatarOk
              ? avatar.needConsent
                ? `请在「${paneTitle('avatar')}」上传真人形象素材并确认授权`
                : `请在「${paneTitle('avatar')}」选一张模特图（AI 模特图产出或自己上传）`
              : /**
                 * ⚠️ 2026-10-09：口播稿**不再是唯一**的口播来源。
                 * 配了配音之后，那句「请填写口播稿」是假话 —— 用户手里那条音频
                 * 就是台词，他去填稿子反而是白填（而且成片不会念它）。
                 * 所以这里把两条路一起说出来，并**点名它是哪一格**。
                 */
                !scriptOk && !audioOk
                ? `请在「${paneTitle('script')}」写口播稿，或从素材库选一条配音当台词`
                : undefined
          }
          label={`生成 ${form.duration} 秒${active.name}`}
          onGenerate={doGenerate}
        />
      </CardBody>
    </Card>
  )

  /**
   * 右栏「本页历史」的**三个档**（2026-10-09）。
   *
   * 起因是用户报障：在这一页做的**分镜图**与**配音**，历史里一条都看不到
   * （原话「历史记录那边没出现怎么搞，我知道是不同类型」）。
   * 根因不是丢数据 —— 三档都正常落库、正常计费 —— 而是**这一页的三个档把记录
   * 写在了两种页面键下**：成片写 `appSlug`（`digital-${mode}`），分镜图与配音
   * 写钉死的 `digital-human`（各自的理由见 `BOARD_PAGE` / `VOICEOVER_PAGE` 的注释）。
   * 右栏只读第一个键，后两档因此永远进不来 —— 而且进不来是**静默**的。
   *
   * ⚠️ 这里**只多给一份分档取数**，`jobs` 一个字没动：
   *   · `jobs` 仍是「结果栏这一栏的任务」—— `isResultJob`、清空范围都从它推导。
   *     把分镜图并进 `jobs`，它们就会混进「本轮产出」，而 `outputsFor` 那边
   *     分镜图是**视频任务的输入不是产出**（见 VideoStudio 的 `STUDIO_VIDEO_SLUGS`）。
   *   · `boardJobs` / `voJobs` 早就取好了（页内的画廊与试听条在用），这里只是
   *     把它们**一并交给右栏**，没有新增任何取数键。
   *
   * `id` 用语义名而不是 `appSlug`：这一页切模式时 `appSlug` 会变（`digital-${mode}`），
   * 若拿它当 id，用户切一下模式、选中的档就跳到别处去了。label 才是会跟着档走的那个。
   */
  const historyTabs = [
    { id: 'video', label: active.name, jobs },
    { id: 'board', label: '分镜图', jobs: boardJobs },
    { id: 'voice', label: '配音', jobs: voJobs },
  ]

  return (
    <div className="page page--wide">
      <GeneratorFrame
        form={
          <WorkflowTabs tabs={tabs} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              activeHint={`数字人视频要等一会儿（${Math.round(estimateMs(form.duration) / 60000)} 分钟左右），可以先去别的页面干活`}
              resultTitle="数字人结果"
              resultEmpty="配好出镜形象与台词（口播稿，或从素材库选一条配音）后提交，产出带口型的口播视频。"
              aspectRatio={form.aspectRatio}
              historyTabs={historyTabs}
              /* 历史记录上的「重做」把该次参数填回左栏（见 lib/refill.ts） */
              onRedo={(job: Job) => {
                patch(refillFromInputs(form, job.inputs))
                toast.success('已把这次任务的参数填回左侧表单 —— 确认后可再次生成')
              }}
            />
          </>
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />

      {/*
        音色库抽屉 —— **它是这一页唯一的音色入口**（页内那颗胶囊只是开门）。

        ⚠️ 「同一字段只许一个入口」：原来那格是一个原生 `<Select>`（88 组 optgroup）
        加一颗 `VoicePreviewButton`；现在整格换成胶囊 + 这个抽屉。
        两边都能改 `form.voice` 的话，用户会以为一个是「筛选」一个是「选定」，
        而它们改的是同一个值。

        与 `/tts` 传的参数逐项一致（同一份 `voices` 状态 → 同一份清单与引擎说明），
        差别只有 `sourcePage` —— 那是用量账本的口径，不能写成别页。
      */}
      <Drawer
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        title="音色库"
        icon="wave"
        width={720}
      >
        <VoiceLibrary
          groups={voices.groups}
          mine={voices.mine}
          value={voiceId}
          onChange={(id) => patch({ voice: id })}
          defaultId={voices.defaultId}
          engineLabel={voices.engineLabel}
          model={voices.model}
          notice={
            voices.source === 'fallback'
              ? `音色清单没读到（${voices.error || '原因不明'}），下面列的是内置兜底 —— 这**不是**全部音色。刷新页面会重新拉取。`
              : null
          }
          sourcePage="digital-human"
        />
      </Drawer>
    </div>
  )
}
